// Main JavaScript for Markdown Editor
// Phase 2: Bootstrap is gone. All overlays now route through Alpine $store.ui.

document.addEventListener("DOMContentLoaded", function () {
  initializeTheme();
  initializeThemeToggle();
  initializeAIAssistance();
  initializeEditorEnhancements();
});

// Theme Management
function initializeTheme() {
  // The user's *preference* (may be 'system'); the early-init script
  // in base.html has already resolved + applied it to <html>.
  const preference = localStorage.getItem("theme") || "light";
  const resolved = resolveThemePreference(preference);

  // Idempotent: only re-apply if <html data-theme> doesn't match.
  if (document.documentElement.getAttribute("data-theme") !== resolved) {
    setTheme(preference);
  }
  updateThemeIcon(preference);
}

// Quick-cycle roster used by the sidebar theme-toggle button.
// Includes 'system' as the first entry; the full picker (popover)
// lives in the topbar and at /settings/appearance.
const THEME_CYCLE = [
  "system",
  // Legacy metaproject themes (preserved for backward compat)
  "light",
  "dark",
  "dark-brown",
  // Dark themes
  "precision",
  "workshop",
  "midnight",
  "forge",
  "graphite",
  "amethyst",
  "carbon",
  "solarized-dark",
  "nord",
  "catppuccin-mocha",
  "catppuccin-macchiato",
  "tokyo-night",
  "tokyo-night-storm",
  "gruvbox-dark",
  "gruvbox-material",
  "everforest-dark",
  "kanagawa-wave",
  "kanagawa-dragon",
  "rose-pine",
  "rose-pine-moon",
  "dracula-soft",
  "night-owl",
  "ayu-mirage",
  "palenight",
  // Ported from nexus electron (dark)
  "default",
  "claude",
  "opencode",
  "amoled",
  "matrix",
  "synthwave84",
  "shadesofpurple",
  "cobalt2",
  "vesper",
  "zenburn",
  "cinnabar",
  "lucent-orng",
  "carbonfox",
  "mercury",
  "catppuccin-frappe",
  "aura",
  "latex-dark",
  "graph-paper-dark",
  "theorem-dark",
  "forest-floor",
  "tide",
  "autumn",
  "broadsheet-dark",
  "journal-dark",
  // Light themes
  "warehouse",
  "daylight",
  "linen",
  "aurora",
  "mono",
  "coral",
  "solarized-light",
  "nord-light",
  "gruvbox-light",
  "rose-pine-dawn",
  "catppuccin-latte",
  "kanagawa-lotus",
  // Ported from nexus electron (light)
  "vercel",
  "matrix-light",
  "parchment",
  "sandstone",
  "meadow",
  "pebble",
  "broadsheet-light",
  "manuscript",
  "arctic",
  "glacier",
  "paper-light",
  "concrete",
  "fog",
  "latex-light",
  "graph-paper-light",
  "thesis",
  "cotton-candy",
  "macaron",
  "blueprint",
  "graphite-light",
  "electric-light",
  "fluorescent",
  "bauhaus",
  "vapor",
];

// Source of truth for "is this theme dark-scheme?". Mirrors the set
// defined inline in base.html's pre-hydration script — keep in sync.
const DARK_THEMES =
  window.__DARK_THEMES__ ||
  new Set([
    "dark",
    "dark-brown",
    "precision",
    "workshop",
    "midnight",
    "forge",
    "graphite",
    "amethyst",
    "carbon",
    "solarized-dark",
    "nord",
    "catppuccin-mocha",
    "catppuccin-macchiato",
    "tokyo-night",
    "tokyo-night-storm",
    "gruvbox-dark",
    "gruvbox-material",
    "everforest-dark",
    "kanagawa-wave",
    "kanagawa-dragon",
    "rose-pine",
    "rose-pine-moon",
    "dracula-soft",
    "night-owl",
    "ayu-mirage",
    "palenight",
    "default",
    "claude",
    "opencode",
    "amoled",
    "matrix",
    "synthwave84",
    "shadesofpurple",
    "cobalt2",
    "vesper",
    "zenburn",
    "cinnabar",
    "lucent-orng",
    "carbonfox",
    "mercury",
    "catppuccin-frappe",
    "aura",
    "latex-dark",
    "graph-paper-dark",
    "theorem-dark",
    "forest-floor",
    "tide",
    "autumn",
    "broadsheet-dark",
    "journal-dark",
  ]);

// Resolve 'system' preference to a concrete theme name.
function resolveThemePreference(pref) {
  if (pref !== "system") return pref;
  const dark =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  return dark ? "precision" : "warehouse";
}

// Re-apply the resolved theme if the OS preference flips while the
// user is on 'system'. Other preferences are unaffected.
(function watchSystemPreference() {
  if (!window.matchMedia) return;
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    const pref = localStorage.getItem("theme") || "light";
    if (pref === "system") setTheme("system");
  };
  if (media.addEventListener) media.addEventListener("change", handler);
  else if (media.addListener) media.addListener(handler);
})();

function initializeThemeToggle() {
  // Bind every theme-toggle button. Authenticated (sidebar) and
  // unauthenticated (topbar) chrome each render their own button; they share
  // the .js-theme-toggle class but carry distinct ids (no duplicate-id).
  const toggles = document.querySelectorAll(".js-theme-toggle");
  toggles.forEach(function (btn) {
    btn.addEventListener("click", function () {
      const currentPref = localStorage.getItem("theme") || "light";
      const idx = THEME_CYCLE.indexOf(currentPref);
      const newTheme = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length];
      setTheme(newTheme);
    });
  });
}

// Exposed on window so the appearance page (appearance.js) can call it.
window.setTheme = setTheme;

// Exposed for the command palette "Cycle theme" quick action — advances one
// step through THEME_CYCLE, mirroring the sidebar .js-theme-toggle button.
window.cyclePaletteTheme = function () {
  const currentPref = localStorage.getItem("theme") || "light";
  const idx = THEME_CYCLE.indexOf(currentPref);
  setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]);
};

function setTheme(theme) {
  // Store the user's preference verbatim (may be 'system').
  localStorage.setItem("theme", theme);
  const resolved = resolveThemePreference(theme);
  document.documentElement.setAttribute("data-theme", resolved);
  document.body.setAttribute("data-theme", resolved);
  window.__THEME_PREFERENCE__ = theme;

  // data-theme-mode="dark" drives editor/chat-widget/form dark-mode CSS
  // variants (style.css, editor-plane.css). Set on dark themes, removed
  // on light. The token system handles the rest via --bg-*/--text-* vars.
  if (DARK_THEMES.has(resolved)) {
    document.documentElement.setAttribute("data-theme-mode", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme-mode");
  }

  // Swap the dynamically loaded theme CSS (injected by base.html early script).
  // Without this, the active theme file stays on the previous theme after switching.
  var themeLink = document.getElementById("active-theme-css");
  // Same cache-busting token base.html stamped on the initial <link>;
  // without it a switched-to theme can come back from cache stale.
  var themeV = window.THEME_ASSET_V ? "?v=" + window.THEME_ASSET_V : "";
  var newHref = "/static/css/tokens/themes/" + resolved + ".css" + themeV;
  if (themeLink) {
    themeLink.href = newHref;
  } else {
    // Fallback: link was removed or missing — re-create it.
    var link = document.createElement("link");
    link.rel = "stylesheet";
    link.id = "active-theme-css";
    link.href = newHref;
    document.head.appendChild(link);
  }

  updateThemeIcon(theme);

  if (window.mermaidRenderer) {
    window.mermaidRenderer.updateTheme(resolved);
  }

  // Notify any UI listening for theme changes (e.g. appearance page).
  window.dispatchEvent(
    new CustomEvent("themechange", {
      detail: { preference: theme, resolved: resolved },
    }),
  );
}

function updateThemeIcon(theme) {
  // The Lucide hydrator replaces the placeholder <i> with an <svg>; swap by
  // replacing the host button's innerHTML rather than mutating className.
  const hosts = document.querySelectorAll(".js-theme-toggle");
  if (!hosts.length || !window.swapIcon) return;
  // Icon reflects the user's *preference*, not the resolved theme.
  let lucideName;
  if (theme === "system") lucideName = "circle";
  else if (theme === "dark") lucideName = "moon";
  else if (theme === "dark-brown") lucideName = "moon-star";
  else if (theme === "light") lucideName = "sun";
  else if (DARK_THEMES.has(theme)) lucideName = "moon";
  else lucideName = "sun";
  hosts.forEach(function (host) { window.swapIcon(host, lucideName); });
}

// ===============================
// Custom Confirmation Dialog
// ===============================

/**
 * Show a custom confirmation dialog
 * @param {Object} options - Configuration options
 * @param {string} options.title - Modal title
 * @param {string} options.message - Main message to display
 * @param {string} [options.details] - Additional details (optional)
 * @param {string} [options.type='danger'] - Type: 'danger', 'warning', 'info'
 * @param {string} [options.confirmText='Confirm'] - Confirm button text
 * @param {string} [options.cancelText='Cancel'] - Cancel button text
 * @param {string} [options.confirmClass='btn-danger'] - Confirm button class
 * @param {string} [options.icon] - Custom icon class (auto-detected if not provided)
 * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
 */
function showCustomConfirm(options) {
  return new Promise((resolve) => {
    // The dialog markup lives in base.html and is driven by the Alpine
    // $store.ui store. Esc / backdrop / X all route through #confirmCancelBtn
    // (see base.html), so binding the two buttons covers every close path.
    const store =
      window.Alpine && window.Alpine.store && window.Alpine.store("ui");

    // Default options
    const config = {
      title: "Confirm Action",
      message: "Are you sure you want to proceed?",
      details: "",
      type: "danger", // danger, warning, info
      confirmText: "Confirm",
      cancelText: "Cancel",
      icon: null,
      ...options,
    };

    // Set title and message
    document.getElementById("customConfirmModalLabel").textContent =
      config.title;
    document.getElementById("confirmMessage").textContent = config.message;

    // Handle details
    const detailsElement = document.getElementById("confirmDetails");
    if (config.details) {
      detailsElement.textContent = config.details;
      detailsElement.style.display = "block";
    } else {
      detailsElement.style.display = "none";
    }

    // Set icon and styling based on type
    const iconContainer = document.querySelector(".confirm-icon-container");
    const iconElement = document.getElementById("confirmIcon");
    const confirmBtn = document.getElementById("confirmActionBtn");
    const cancelBtn = document.getElementById("confirmCancelBtn");

    // Reset type state. The confirm button keeps its base utility classes
    // (set in base.html); only the token background colour is toggled here.
    // These literal class names are scanned by Tailwind from this file.
    iconContainer.classList.remove("warning", "info");
    confirmBtn.classList.remove("bg-error", "bg-warning", "bg-accent");

    // config.icon (if supplied) is now a Lucide name (e.g. 'trash-2').
    let _lucide;
    if (config.type === "warning") {
      iconContainer.classList.add("warning");
      _lucide = config.icon || "alert-triangle";
      confirmBtn.classList.add("bg-warning");
    } else if (config.type === "info") {
      iconContainer.classList.add("info");
      _lucide = config.icon || "info";
      confirmBtn.classList.add("bg-accent");
    } else {
      // danger (default)
      _lucide = config.icon || "alert-triangle";
      confirmBtn.classList.add("bg-error");
    }
    if (window.swapIcon) window.swapIcon(iconContainer, _lucide);

    // Set button texts
    confirmBtn.querySelector("span").textContent = config.confirmText;
    cancelBtn.querySelector("span").textContent = config.cancelText;

    // Handle button clicks
    const cleanup = () => {
      confirmBtn.removeEventListener("click", handleConfirm);
      cancelBtn.removeEventListener("click", handleCancel);
    };
    const handleConfirm = () => {
      cleanup();
      if (store) store.closeModal();
      resolve(true);
    };
    const handleCancel = () => {
      cleanup();
      if (store) store.closeModal();
      resolve(false);
    };

    confirmBtn.addEventListener("click", handleConfirm);
    cancelBtn.addEventListener("click", handleCancel);

    // Show modal + focus the confirm button.
    if (store) store.openModal("customConfirmModal");
    setTimeout(() => confirmBtn.focus(), 50);
  });
}

/**
 * Quick confirmation for delete actions
 * @param {string} itemName - Name of the item being deleted
 * @param {string} [customMessage] - Custom message override
 * @returns {Promise<boolean>}
 */
function confirmDelete(itemName, customMessage = null) {
  return showCustomConfirm({
    title: "Confirm Deletion",
    message: customMessage || `Are you sure you want to delete "${itemName}"?`,
    details: "This action cannot be undone.",
    type: "danger",
    confirmText: "Delete",
    cancelText: "Cancel",
    icon: "trash-2",
  });
}

/**
 * Quick confirmation for archive actions
 * @param {string} itemName - Name of the item being archived
 * @param {string} [customMessage] - Custom message override
 * @returns {Promise<boolean>}
 */
function confirmArchive(itemName, customMessage = null) {
  return showCustomConfirm({
    title: "Confirm Archive",
    message: customMessage || `Are you sure you want to archive "${itemName}"?`,
    details: "You can restore this item later if needed.",
    type: "warning",
    confirmText: "Archive",
    cancelText: "Cancel",
    icon: "archive",
  });
}

/**
 * Quick confirmation for important actions
 * @param {string} actionName - Name of the action
 * @param {string} [customMessage] - Custom message override
 * @returns {Promise<boolean>}
 */
function confirmAction(actionName, customMessage = null) {
  return showCustomConfirm({
    title: "Confirm Action",
    message: customMessage || `Are you sure you want to ${actionName}?`,
    type: "info",
    confirmText: "Continue",
    cancelText: "Cancel",
    icon: "check-circle",
  });
}

/**
 * confirmThenDispatch — generic two-stage click handler used by the
 * `confirm_button()` Jinja macro (templates/components/button.html).
 *
 * Markup contract:
 *   <button data-action="confirmThenDispatch"
 *           data-args='{"kind":"delete","target":"story",
 *                       "message":"","handler":"lvDeleteStory",
 *                       "handlerArgs":"42"}'>Delete</button>
 *
 * Flow:
 *   1. Parse JSON payload from `this.dataset.args` (NOT the positional
 *      args from csp-dispatcher — its comma-split parser would mangle
 *      the JSON braces).
 *   2. Open the shared customConfirmModal via confirmDelete /
 *      confirmArchive / confirmAction based on `kind`.
 *   3. On confirm → look up window[handler] and invoke it with
 *      handlerArgs (comma-split + heuristic-typed, matching
 *      csp-dispatcher's parseArg semantics so callers can rely on the
 *      same conventions). On cancel → no-op.
 *
 * Bound as `this = <button element>` by csp-dispatcher (fn.apply(t, …)).
 */
function confirmThenDispatch() {
  const el = this;
  if (!el || !el.dataset) return;

  let payload;
  try {
    payload = JSON.parse(el.dataset.args || "{}");
  } catch (err) {
    if (window.console && console.error) {
      console.error("[confirmThenDispatch] invalid JSON in data-args:", err);
    }
    return;
  }

  const kind = payload.kind || "action";
  const target = payload.target || "";
  const message = payload.message || null;
  const handler = payload.handler || "";
  const handlerArgs = payload.handlerArgs;

  // Mirror csp-dispatcher.js parseArg heuristic so handlerArgs use the
  // same conventions as data-args (numbers / booleans / __none__).
  function parseArg(raw) {
    const t = String(raw).trim();
    if (t === "__none__") return null;
    if (t === "__true__") return true;
    if (t === "__false__") return false;
    if (t === "true") return true;
    if (t === "false") return false;
    if (/^-?\d+$/.test(t)) return Number(t);
    if (/^-?\d*\.\d+$/.test(t)) return Number(t);
    return t;
  }
  function toArgsArray(raw) {
    if (raw === undefined || raw === null || raw === "") return [];
    if (Array.isArray(raw)) return raw;
    return String(raw).split(",").map(parseArg);
  }

  let confirmPromise;
  if (kind === "delete") {
    confirmPromise = confirmDelete(target, message);
  } else if (kind === "archive") {
    confirmPromise = confirmArchive(target, message);
  } else {
    confirmPromise = confirmAction(target, message);
  }

  confirmPromise.then((ok) => {
    if (!ok) return; // cancel → no-op
    if (!handler) return;
    const fn = window[handler];
    if (typeof fn !== "function") {
      if (window.console && console.warn) {
        console.warn(
          "[confirmThenDispatch] missing handler: window." + handler,
        );
      }
      return;
    }
    try {
      fn.apply(el, toArgsArray(handlerArgs));
    } catch (err) {
      if (window.console && console.error) {
        console.error("[confirmThenDispatch] " + handler + " threw:", err);
      }
    }
  });
}

// ===============================
// AI Assistance functionality
// ===============================
// NOTE: All AI document updates are now performed via backend tools (React agent endpoints) only.
// The frontend never overwrites the document with raw AI output. This ensures all updates are
// permission-checked, versioned, and tool-confirmed.

function initializeAIAssistance() {
  const editor = document.getElementById("content");
  if (!editor) return;

  let aiProcessing = false;
  let aiAbortController = null;
  let aiLoadingInterval = null;
  let aiEscapeListener = null;

  // Add keydown event listener for Enter key detection
  editor.addEventListener("keydown", function (e) {
    if (aiProcessing) return;

    // Check for Enter key (keyCode 13 or key 'Enter')
    if (e.key === "Enter" || e.keyCode === 13) {
      const text = editor.value;
      const cursorPos = editor.selectionStart;

      // Look for // command pattern (case insensitive) - support both with and without space
      const beforeCursor = text.substring(0, cursorPos);
      const lines = beforeCursor.split("\n");
      const currentLine = lines[lines.length - 1];
      const aiMatch = currentLine.match(/^\/\/\s*(.+)$/);

      if (aiMatch) {
        // User pressed Enter after // command
        e.preventDefault();
        const commandStartPos = cursorPos - currentLine.length;
        const commandEndPos = cursorPos;
        handleAIRequestStreaming(aiMatch[1], commandStartPos, commandEndPos);
        return;
      }
    }
  });

  editor.addEventListener("input", function (e) {
    if (aiProcessing) return;

    const text = editor.value;
    const cursorPos = editor.selectionStart;

    // Look for // command pattern (case insensitive) - support both with and without space
    const beforeCursor = text.substring(0, cursorPos);
    const lines = beforeCursor.split("\n");
    const currentLine = lines[lines.length - 1];
    const aiMatch = currentLine.match(/^\/\/\s*(.+)$/);

    // Check for Enter key press after // command (fallback)
    if (
      aiMatch &&
      ((e.inputType === "insertText" && e.data === "\n") ||
        e.inputType === "insertLineBreak" ||
        e.inputType === "insertParagraph")
    ) {
      // User pressed Enter after // command
      e.preventDefault();
      const commandStartPos = cursorPos - currentLine.length;
      const commandEndPos = cursorPos;
      handleAIRequestStreaming(aiMatch[1], commandStartPos, commandEndPos);
    }
  });

  function cancelAIProcessing() {
    if (!aiProcessing) return;

    // Cancel the fetch request
    if (aiAbortController) {
      aiAbortController.abort();
      aiAbortController = null;
    }

    // Clear the loading animation
    if (aiLoadingInterval) {
      clearInterval(aiLoadingInterval);
      aiLoadingInterval = null;
    }

    // Remove escape key listener
    if (aiEscapeListener) {
      document.removeEventListener("keydown", aiEscapeListener);
      aiEscapeListener = null;
    }

    // Restore original editor styling
    const editor = document.getElementById("content");
    if (editor) {
      editor.style.color = "";
      editor.style.backgroundColor = "";
      // Restore original content and cursor position
      if (
        typeof originalContent !== "undefined" &&
        typeof originalCursorPos !== "undefined"
      ) {
        editor.value = originalContent;
        editor.setSelectionRange(originalCursorPos, originalCursorPos);
      }
    }

    // Reset processing state
    aiProcessing = false;

    // Show notification
    showNotification("🛑 AI processing cancelled", "warning");
  }

  async function handleAIRequest(prompt, startPos, endPos) {
    if (aiProcessing) return;
    aiProcessing = true;

    const editor = document.getElementById("content");
    const documentId = window.currentDocumentId;

    // Create abort controller for cancelling the request
    aiAbortController = new AbortController();

    // Add escape key listener to cancel processing
    aiEscapeListener = function (e) {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        cancelAIProcessing();
      }
    };
    document.addEventListener("keydown", aiEscapeListener);

    // Check for document rewrite commands
    const isDocumentRewrite =
      prompt.toLowerCase().trim() === "document" ||
      [
        "update document",
        "rewrite",
        "replace document",
        "new version",
        "complete rewrite",
      ].some((keyword) => prompt.toLowerCase().includes(keyword));

    // Store original cursor position
    const originalCursorPos = editor.selectionStart;

    // Create processing message with blue styling (inline since we removed CSS class)
    const processingMessage = "Processing...";

    // Replace the command line with processing message
    const beforeCommand = editor.value.substring(0, startPos);
    const afterCommand = editor.value.substring(endPos);

    // Store original content for error recovery
    const originalContent = editor.value;

    // Set initial processing text
    editor.value = beforeCommand + processingMessage + afterCommand;

    // Set cursor position after processing text to prevent jumping
    const newCursorPos = startPos + processingMessage.length;
    editor.setSelectionRange(newCursorPos, newCursorPos);
    editor.focus();

    // Add visual styling to processing text by temporarily changing editor style
    const originalColor = editor.style.color;
    const originalBackground = editor.style.backgroundColor;

    // Apply blue styling to show processing
    editor.style.color = "var(--status-info)";
    editor.style.backgroundColor = "var(--status-info-subtle)";

    // Animate loading dots
    let loadingDots = "";
    aiLoadingInterval = setInterval(() => {
      if (!aiProcessing) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
        return;
      }

      loadingDots = loadingDots.length >= 3 ? "" : loadingDots + ".";
      const animatedText = `Processing${loadingDots}`;

      // Only update if cursor is still at processing text
      const currentValue = editor.value;
      const currentBeforeCommand = currentValue.substring(0, startPos);
      const currentAfterCommand = currentValue.substring(
        startPos + animatedText.length,
      );

      // Check if processing text is still there
      if (
        currentValue
          .substring(startPos, startPos + animatedText.length)
          .startsWith("Processing")
      ) {
        editor.value =
          currentBeforeCommand + animatedText + currentAfterCommand;
        // Keep cursor at end of processing text
        const cursorPos = startPos + animatedText.length;
        editor.setSelectionRange(cursorPos, cursorPos);
      }
    }, 500);

    // Show notification with escape hint
    showNotification(
      "🤖 AI is processing your request... (Press Escape to cancel)",
      "info",
    );

    // Show thinking section
    showAIThinking("Analyzing document context...");

    try {
      // For //document command, pass empty content to get a fresh document
      const contextContent = isDocumentRewrite
        ? ""
        : beforeCommand + afterCommand;

      // Use React agent endpoint ONLY (tool-based updates)
      const response = await fetch("/documents/api/ai-assist-react", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin", // Include cookies for authentication
        signal: aiAbortController.signal, // Add abort signal
        body: JSON.stringify({
          prompt: isDocumentRewrite
            ? `Create a complete new document about: ${prompt}`
            : prompt,
          document_id: documentId,
          document_content: contextContent,
        }),
      });

      // Clear the loading animation
      if (aiLoadingInterval) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
      }

      // Hide thinking section
      hideAIThinking();

      // Remove escape key listener
      if (aiEscapeListener) {
        document.removeEventListener("keydown", aiEscapeListener);
        aiEscapeListener = null;
      }

      // Restore original editor styling
      editor.style.color = originalColor;
      editor.style.backgroundColor = originalBackground;

      // Check if response is JSON or HTML (redirect)
      let data;
      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        // Handle non-200 responses
        if (
          response.status === 302 ||
          response.status === 401 ||
          response.status === 403
        ) {
          throw new Error(
            "Authentication required. Please refresh the page and log in.",
          );
        }
        throw new Error(`Server error: ${response.status}`);
      }

      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error("AI JSON parse error:", jsonError);
          throw new Error("Invalid response from server. Please try again.");
        }
      } else {
        // If we get HTML instead of JSON, it's likely a redirect to login
        console.error("AI non-JSON response, content-type:", contentType);
        throw new Error(
          "Authentication required. Please refresh the page and log in.",
        );
      }

      if (data.success) {
        // Only update the editor if updated_content is present (tool-based update)
        if (
          data.updated_content !== undefined &&
          data.updated_content !== null
        ) {
          if (isDocumentRewrite) {
            editor.value = data.updated_content;
            editor.setSelectionRange(0, 0);
          } else {
            // Find and replace the processing text more reliably
            const currentValue = editor.value;
            const processingTextStart = currentValue.indexOf("Processing");
            if (processingTextStart !== -1) {
              const processingMatch = currentValue
                .substring(processingTextStart)
                .match(/^Processing\.{0,3}/);
              if (processingMatch) {
                const processingTextEnd =
                  processingTextStart + processingMatch[0].length;
                const beforeProcessing = currentValue.substring(
                  0,
                  processingTextStart,
                );
                const afterProcessing =
                  currentValue.substring(processingTextEnd);
                editor.value =
                  beforeProcessing + data.updated_content + afterProcessing;
                const newCursorPos =
                  processingTextStart + data.updated_content.length;
                editor.setSelectionRange(newCursorPos, newCursorPos);
              }
            }
          }
          if (typeof updatePreview === "function") {
            updatePreview();
          }
          showNotification("✅ Document updated successfully!", "success");
        } else {
          // No updated_content provided, show a warning and do not update the editor
          showNotification(
            "⚠️ AI did not return updated content. No changes made.",
            "warning",
          );
        }
      } else {
        // Clear processing text and restore original content on error
        editor.value = originalContent;
        editor.setSelectionRange(originalCursorPos, originalCursorPos);

        const errorMsg = data.error || "Unknown error occurred";
        showNotification(`❌ AI Error: ${errorMsg}`, "error");
      }

      // After receiving the AI response (assuming variable is 'data')
      // (intermediate_steps available on data when present — used for diagnostics by callers)
    } catch (error) {
      console.error("AI request failed:", error);

      // Hide thinking section
      hideAIThinking();

      // Clear the loading animation
      if (aiLoadingInterval) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
      }

      // Remove escape key listener
      if (aiEscapeListener) {
        document.removeEventListener("keydown", aiEscapeListener);
        aiEscapeListener = null;
      }

      // Restore original editor styling
      editor.style.color = originalColor;
      editor.style.backgroundColor = originalBackground;

      // Check if it was an abort (user cancelled)
      if (error.name === "AbortError") {
        // Restore original content and cursor position
        editor.value = originalContent;
        editor.setSelectionRange(originalCursorPos, originalCursorPos);
        showNotification("AI request cancelled", "info");
        return;
      }

      // Restore original content and cursor position on any error
      editor.value = originalContent;
      editor.setSelectionRange(originalCursorPos, originalCursorPos);

      const errorMessage =
        error.message || "AI request failed. Please try again.";
      showNotification(`❌ ${errorMessage}`, "error");
    } finally {
      aiProcessing = false;
      aiAbortController = null;

      // Ensure thinking section is hidden
      hideAIThinking();

      // Ensure styling is restored
      editor.style.color = originalColor;
      editor.style.backgroundColor = originalBackground;

      // Ensure cleanup
      if (aiLoadingInterval) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
      }

      if (aiEscapeListener) {
        document.removeEventListener("keydown", aiEscapeListener);
        aiEscapeListener = null;
      }
    }
  }

  async function handleAIRequestStreaming(prompt, startPos, endPos) {
    if (aiProcessing) return;
    aiProcessing = true;

    const editor = document.getElementById("content");
    const documentId = window.currentDocumentId;

    // Initialize streaming variables
    let hasStartedReceiving = false;
    let accumulatedResponse = "";
    let lastPreviewUpdate = 0;
    const PREVIEW_THROTTLE_MS = 500;

    // Create abort controller for cancelling the request
    aiAbortController = new AbortController();

    // Add escape key listener to cancel processing
    aiEscapeListener = function (e) {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        cancelAIProcessing();
      }
    };
    document.addEventListener("keydown", aiEscapeListener);

    // Check for document rewrite commands
    const isDocumentRewrite =
      prompt.toLowerCase().trim() === "document" ||
      [
        "update document",
        "rewrite",
        "replace document",
        "new version",
        "complete rewrite",
      ].some((keyword) => prompt.toLowerCase().includes(keyword));

    // Store original cursor position
    const originalCursorPos = editor.selectionStart;

    // Create processing message with blue styling
    const processingMessage = "Processing...";

    // Replace the command line with processing message
    const beforeCommand = editor.value.substring(0, startPos);
    const afterCommand = editor.value.substring(endPos);

    // Store original content for error recovery
    const originalContent = editor.value;

    // Set initial processing text
    editor.value = beforeCommand + processingMessage + afterCommand;

    // Set cursor position after processing text
    const newCursorPos = startPos + processingMessage.length;
    editor.setSelectionRange(newCursorPos, newCursorPos);
    editor.focus();

    // Add visual styling to processing text
    const originalColor = editor.style.color;
    const originalBackground = editor.style.backgroundColor;

    // Apply blue styling to show processing
    editor.style.color = "var(--status-info)";
    editor.style.backgroundColor = "var(--status-info-subtle)";

    // Animate loading dots
    let loadingDots = "";
    aiLoadingInterval = setInterval(() => {
      if (!aiProcessing) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
        return;
      }

      loadingDots = loadingDots.length >= 3 ? "" : loadingDots + ".";
      const animatedText = `Processing${loadingDots}`;

      // Only update if cursor is still at processing text
      const currentValue = editor.value;
      const currentBeforeCommand = currentValue.substring(0, startPos);
      const currentAfterCommand = currentValue.substring(
        startPos + animatedText.length,
      );

      // Check if processing text is still there
      if (
        currentValue
          .substring(startPos, startPos + animatedText.length)
          .startsWith("Processing")
      ) {
        editor.value =
          currentBeforeCommand + animatedText + currentAfterCommand;
        // Keep cursor at end of processing text
        const cursorPos = startPos + animatedText.length;
        editor.setSelectionRange(cursorPos, cursorPos);
      }
    }, 500);

    // Show notification with escape hint
    showNotification(
      "🤖 AI is processing your request... (Press Escape to cancel)",
      "info",
    );

    // Show thinking section
    showAIThinking("Analyzing document context...");

    try {
      // For //document command, pass empty content to get a fresh document
      const contextContent = isDocumentRewrite
        ? ""
        : beforeCommand + afterCommand;

      // Use React agent streaming endpoint ONLY (tool-based updates)
      const requestData = {
        prompt: isDocumentRewrite
          ? `Create a complete new document about: ${prompt}`
          : prompt,
        document_id: documentId,
        document_content: contextContent,
        prompt_template_id: currentPromptTemplateId,
        agent_type: currentAgentType, // Include selected agent type
      };

      // Always use the standard React agent endpoint with appropriate template
      // The template ID determines which agent is used
      const endpoint = "/documents/api/ai-assist-react-stream";

      // Use fetch with streaming for Server-Sent Events
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        signal: aiAbortController.signal,
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let finalData = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "chunk") {
                // Clear loading animation on first chunk
                if (!hasStartedReceiving) {
                  hasStartedReceiving = true;
                  hideAIThinking();
                  if (aiLoadingInterval) {
                    clearInterval(aiLoadingInterval);
                    aiLoadingInterval = null;
                  }

                  // Restore original editor styling
                  editor.style.color = originalColor;
                  editor.style.backgroundColor = originalBackground;

                  // Clear processing text and start with AI response
                  const currentValue = editor.value;
                  const processingTextStart =
                    currentValue.indexOf("Processing");

                  if (processingTextStart !== -1) {
                    const processingMatch = currentValue
                      .substring(processingTextStart)
                      .match(/^Processing\.{0,3}/);
                    if (processingMatch) {
                      const processingTextEnd =
                        processingTextStart + processingMatch[0].length;
                      const beforeProcessing = currentValue.substring(
                        0,
                        processingTextStart,
                      );
                      const afterProcessing =
                        currentValue.substring(processingTextEnd);

                      if (isDocumentRewrite) {
                        // For document rewrite, replace entire content
                        editor.value = data.content;
                        accumulatedResponse = data.content;
                        // Start AI streaming highlight for entire content
                        if (typeof window.startAIStreaming === "function") {
                          window.startAIStreaming(0);
                          window.updateAIStreamingRange(data.content.length);
                        }
                      } else {
                        // For regular assistance, replace processing text
                        editor.value =
                          beforeProcessing + data.content + afterProcessing;
                        accumulatedResponse = data.content;
                        // Start AI streaming highlight for the response
                        if (typeof window.startAIStreaming === "function") {
                          window.startAIStreaming(startPos);
                          window.updateAIStreamingRange(
                            startPos + data.content.length,
                          );
                        }
                      }
                    }
                  }
                } else {
                  // Append new content
                  accumulatedResponse += data.content;

                  if (isDocumentRewrite) {
                    editor.value = accumulatedResponse;
                    editor.setSelectionRange(
                      accumulatedResponse.length,
                      accumulatedResponse.length,
                    );
                    // Update AI streaming highlight range
                    if (typeof window.updateAIStreamingRange === "function") {
                      window.updateAIStreamingRange(accumulatedResponse.length);
                    }
                  } else {
                    // Find where our response starts and update it
                    const currentValue = editor.value;
                    const responseStart = startPos;
                    const responseEnd =
                      responseStart + accumulatedResponse.length;
                    const beforeResponse = beforeCommand;
                    const afterResponse = afterCommand;

                    editor.value =
                      beforeResponse + accumulatedResponse + afterResponse;
                    editor.setSelectionRange(responseEnd, responseEnd);
                    // Update AI streaming highlight range
                    if (typeof window.updateAIStreamingRange === "function") {
                      window.updateAIStreamingRange(responseEnd);
                    }
                  }
                }

                // Update preview in real-time during AI streaming (with throttling)
                const now = Date.now();
                if (now - lastPreviewUpdate >= PREVIEW_THROTTLE_MS) {
                  lastPreviewUpdate = now;

                  if (typeof window.updatePreview === "function") {
                    try {
                      await window.updatePreview();
                    } catch (error) {
                      console.warn("Preview update failed:", error);
                    }
                  } else {
                    // Fallback: directly update preview if function not available
                    const previewElement =
                      document.getElementById("markdown-preview");
                    if (previewElement && typeof marked !== "undefined") {
                      try {
                        const html = marked.parse(editor.value);
                        previewElement.innerHTML = _sanitizeFormattedHTML(html);
                      } catch (error) {
                        console.warn("Fallback preview update failed:", error);
                      }
                    }
                  }
                }
              } else if (data.type === "complete") {
                finalData = data;
                hideAIThinking();
                break;
              } else if (data.type === "error") {
                hideAIThinking();
                // Check if it's an overload error
                if (
                  data.message &&
                  (data.message.includes("overloaded") ||
                    data.message.includes("529"))
                ) {
                  throw new Error(
                    "🔄 AI service is experiencing high demand. Please try again in a few moments.",
                  );
                } else {
                  throw new Error(data.message || "Unknown streaming error");
                }
              } else if (data.type === "status") {
                // Check if it's a retry status
                if (data.message && data.message.includes("overloaded")) {
                  updateAIThinking("🔄 AI service busy, retrying...");
                } else {
                  updateAIThinking(
                    data.message || "Processing with AI agent...",
                  );
                }
              } else if (data.type === "thinking") {
                // Handle verbose thinking process messages
                updateAIThinking(data.content || "AI is processing...");

                // Track count for thinking messages (used by chat UI)
                if (!window.thinkingMessageCount)
                  window.thinkingMessageCount = 0;
                window.thinkingMessageCount++;

                // Add thinking message to chat interface
                try {
                  addThinkingMessageToChat(data.step, data.content);
                } catch (error) {
                  console.error("Error in addThinkingMessageToChat:", error);
                }
              }
            } catch (parseError) {
              console.warn("Failed to parse SSE data:", line, parseError);
            }
          }
        }
      }

      // Remove escape key listener
      if (aiEscapeListener) {
        document.removeEventListener("keydown", aiEscapeListener);
        aiEscapeListener = null;
      }

      // Complete AI streaming highlighting
      if (typeof window.completeAIStreaming === "function") {
        window.completeAIStreaming();
      }

      // Final preview update to ensure everything is rendered
      if (typeof window.updatePreview === "function") {
        try {
          await window.updatePreview();
        } catch (error) {
          console.warn("Final preview update failed:", error);
        }
      }

      // After the streaming loop, check for final updated_content
      if (
        finalData &&
        finalData.updated_content !== undefined &&
        finalData.updated_content !== null
      ) {
        editor.value = finalData.updated_content;
        if (typeof updatePreview === "function") updatePreview();
        showNotification("✅ Document updated successfully!", "success");
      } else {
        showNotification(
          "⚠️ AI did not return updated content. No changes made.",
          "warning",
        );
      }
    } catch (error) {
      console.error("AI streaming request failed:", error);

      // Hide thinking section
      hideAIThinking();

      // Clear the loading animation
      if (aiLoadingInterval) {
        clearInterval(aiLoadingInterval);
        aiLoadingInterval = null;
      }

      // Remove escape key listener
      if (aiEscapeListener) {
        document.removeEventListener("keydown", aiEscapeListener);
        aiEscapeListener = null;
      }

      // Restore original editor styling
      editor.style.color = originalColor;
      editor.style.backgroundColor = originalBackground;

      // Restore original content on error
      editor.value = originalContent;
      editor.setSelectionRange(originalCursorPos, originalCursorPos);

      let errorMessage = "Unknown error occurred";
      if (error.name === "AbortError") {
        errorMessage = "Request was cancelled";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showNotification(`❌ AI Error: ${errorMessage}`, "error");
    } finally {
      aiProcessing = false;
      aiAbortController = null;
    }
  }
}

// Enhanced Markdown Editor Improvements
function initializeEditorEnhancements() {
  const editor = document.getElementById("content");
  if (!editor) {
    // Silent return - editor enhancements only needed on editor pages
    return;
  }

  // Initialize splitter dragging (with small delay for DOM readiness)
  setTimeout(() => {
    initializeSplitter();
  }, 100);

  // Keyboard shortcuts
  initializeKeyboardShortcuts(editor);

  // Smart indentation and auto-pairing
  initializeSmartEditing(editor);

  // Enhanced navigation
  initializeNavigation(editor);

  // Word wrap toggle
  initializeWordWrap(editor);

  // Focus mode
  initializeFocusMode(editor);

  // Help dialog
  initializeHelpDialog();
}

function initializeSplitter() {
  const splitter = document.getElementById("editor-splitter");
  const editorPane = document.getElementById("editor-pane");
  const previewPane = document.getElementById("markdown-preview");
  const container = document.querySelector(".editor-split");

  // Check if we're on a page that has the split editor (not template edit page)
  if (!splitter && !editorPane && !previewPane && !container) {
    return;
  }

  if (!splitter || !editorPane || !previewPane || !container) {
    console.warn(
      "Some split editor elements missing, skipping splitter initialization",
    );
    return;
  }

  let isDragging = false;
  let startX = 0;
  let startEditorWidth = 0;
  let containerWidth = 0;

  // Mouse down on splitter
  splitter.addEventListener("mousedown", function (e) {
    e.preventDefault();
    isDragging = true;
    startX = e.clientX;

    // Get initial dimensions
    const containerRect = container.getBoundingClientRect();
    containerWidth = containerRect.width;
    startEditorWidth = editorPane.getBoundingClientRect().width;

    // Add dragging class for visual feedback
    splitter.classList.add("dragging");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    // Attach mouse move and up events to document for better tracking
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  });

  function handleMouseMove(e) {
    if (!isDragging) return;

    e.preventDefault();
    const deltaX = e.clientX - startX;
    const newEditorWidth = startEditorWidth + deltaX;

    // Calculate percentages (account for splitter width)
    const splitterWidth = splitter.getBoundingClientRect().width;
    const availableWidth = containerWidth - splitterWidth;
    const minWidth = 200; // Minimum width for each pane

    // Ensure minimum widths are respected
    if (
      newEditorWidth < minWidth ||
      availableWidth - newEditorWidth < minWidth
    ) {
      return;
    }

    const editorPercent = (newEditorWidth / availableWidth) * 100;
    const previewPercent = 100 - editorPercent;

    // Apply the new flex basis to both panes
    editorPane.style.flex = `0 0 ${editorPercent}%`;
    previewPane.style.flex = `0 0 ${previewPercent}%`;
  }

  function handleMouseUp(e) {
    if (!isDragging) return;

    isDragging = false;

    // Remove visual feedback
    splitter.classList.remove("dragging");
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    // Remove event listeners
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    // Save the current layout to localStorage for persistence
    const editorPercent =
      (editorPane.getBoundingClientRect().width /
        (containerWidth - splitter.getBoundingClientRect().width)) *
      100;
    localStorage.setItem("editor-splitter-position", editorPercent);
  }

  // Restore saved splitter position on page load
  const savedPosition = localStorage.getItem("editor-splitter-position");
  if (savedPosition) {
    const editorPercent = parseFloat(savedPosition);
    const previewPercent = 100 - editorPercent;

    // Apply saved position
    editorPane.style.flex = `0 0 ${editorPercent}%`;
    previewPane.style.flex = `0 0 ${previewPercent}%`;
  }

  // Double-click to reset to 50/50 split
  splitter.addEventListener("dblclick", function (e) {
    e.preventDefault();
    editorPane.style.flex = "1";
    previewPane.style.flex = "1";
    localStorage.removeItem("editor-splitter-position");
  });
}

function initializeKeyboardShortcuts(editor) {
  editor.addEventListener("keydown", function (e) {
    // Ctrl/Cmd + B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === "b") {
      e.preventDefault();
      wrapSelectedText(editor, "**", "**");
      return;
    }

    // Ctrl/Cmd + I for italic
    if ((e.ctrlKey || e.metaKey) && e.key === "i") {
      e.preventDefault();
      wrapSelectedText(editor, "*", "*");
      return;
    }

    // Ctrl/Cmd + K for link
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      insertLink(editor);
      return;
    }

    // Ctrl/Cmd + Shift + C for code block
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "C") {
      e.preventDefault();
      wrapSelectedText(editor, "\n```\n", "\n```\n");
      return;
    }

    // Ctrl/Cmd + ` for inline code
    if ((e.ctrlKey || e.metaKey) && e.key === "`") {
      e.preventDefault();
      wrapSelectedText(editor, "`", "`");
      return;
    }

    // Ctrl/Cmd + Shift + L for bullet list
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "L") {
      e.preventDefault();
      insertListItem(editor, "- ");
      return;
    }

    // Ctrl/Cmd + Shift + O for numbered list
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "O") {
      e.preventDefault();
      insertListItem(editor, "1. ");
      return;
    }

    // Alt + Up/Down for moving lines
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      moveLine(editor, e.key === "ArrowUp" ? -1 : 1);
      return;
    }

    // Ctrl/Cmd + D for duplicate line
    if ((e.ctrlKey || e.metaKey) && e.key === "d") {
      e.preventDefault();
      duplicateLine(editor);
      return;
    }

    // Ctrl/Cmd + / for toggle comment
    if ((e.ctrlKey || e.metaKey) && e.key === "/") {
      e.preventDefault();
      toggleComment(editor);
      return;
    }
  });
}

function initializeSmartEditing(editor) {
  editor.addEventListener("keydown", function (e) {
    // Auto-pairing for brackets, quotes, etc.
    const pairs = {
      "(": ")",
      "[": "]",
      "{": "}",
      '"': '"',
      "'": "'",
      "`": "`",
    };

    if (pairs[e.key] && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const start = editor.selectionStart;
      const end = editor.selectionEnd;

      if (start !== end) {
        // Wrap selection
        e.preventDefault();
        wrapSelectedText(editor, e.key, pairs[e.key]);
        return;
      }

      // Auto-close if at end of line or before whitespace
      const nextChar = editor.value[start];
      if (!nextChar || /\s/.test(nextChar)) {
        e.preventDefault();
        insertText(editor, e.key + pairs[e.key]);
        editor.setSelectionRange(start + 1, start + 1);
        return;
      }
    }

    // Smart Enter for lists
    if (e.key === "Enter") {
      const cursorPos = editor.selectionStart;
      const lineStart = editor.value.lastIndexOf("\n", cursorPos - 1) + 1;
      const currentLine = editor.value.substring(lineStart, cursorPos);

      // Check for list patterns
      const listMatch = currentLine.match(/^(\s*)([-*+]|\d+\.)\s/);
      if (listMatch) {
        e.preventDefault();
        const indent = listMatch[1];
        const marker = listMatch[2];

        // If line is just the marker, remove it
        if (currentLine.trim() === marker) {
          editor.setSelectionRange(lineStart, cursorPos);
          insertText(editor, "");
        } else {
          // Continue the list
          const nextMarker = /\d+\./.test(marker)
            ? parseInt(marker) + 1 + "."
            : marker;
          insertText(editor, "\n" + indent + nextMarker + " ");
        }
        return;
      }

      // Smart indentation for code blocks
      if (currentLine.match(/^\s*```/)) {
        e.preventDefault();
        insertText(editor, "\n\n```");
        const newPos = editor.selectionStart - 4;
        editor.setSelectionRange(newPos, newPos);
        return;
      }
    }

    // Smart Tab for indentation
    if (e.key === "Tab") {
      e.preventDefault();
      const start = editor.selectionStart;
      const end = editor.selectionEnd;

      if (start !== end) {
        // Indent/unindent selection
        indentSelection(editor, !e.shiftKey);
      } else {
        // Insert spaces or handle list indentation
        if (e.shiftKey) {
          unindentLine(editor);
        } else {
          insertText(editor, "  "); // 2 spaces
        }
      }
    }
  });
}

function initializeNavigation(editor) {
  // Add line numbers toggle
  const toolbar = document.querySelector(".toolbar");
  if (toolbar) {
    const lineNumbersBtn = document.createElement("button");
    lineNumbersBtn.type = "button";
    lineNumbersBtn.className = "btn btn-outline-secondary btn-sm";
    lineNumbersBtn.innerHTML =
      '<i data-lucide="list-ordered" class="inline-block h-4 w-4"></i> Line Numbers';
    lineNumbersBtn.title = "Toggle line numbers";

    lineNumbersBtn.addEventListener("click", function () {
      toggleLineNumbers(editor);
    });

    toolbar.appendChild(lineNumbersBtn);
  }

  // Go to line functionality (Ctrl+G)
  editor.addEventListener("keydown", function (e) {
    if ((e.ctrlKey || e.metaKey) && e.key === "g") {
      e.preventDefault();
      showGoToLineDialog(editor);
    }
  });
}

function initializeWordWrap(editor) {
  const toolbar = document.querySelector(".toolbar");
  if (toolbar) {
    const wrapBtn = document.createElement("button");
    wrapBtn.type = "button";
    wrapBtn.className = "btn btn-outline-secondary btn-sm";
    wrapBtn.innerHTML =
      '<i data-lucide="wrap-text" class="inline-block h-4 w-4"></i> Wrap';
    wrapBtn.title = "Toggle word wrap";

    wrapBtn.addEventListener("click", function () {
      const isWrapped = editor.style.whiteSpace === "pre-wrap";
      editor.style.whiteSpace = isWrapped ? "pre" : "pre-wrap";
      editor.style.overflowX = isWrapped ? "auto" : "hidden";
      wrapBtn.classList.toggle("active", !isWrapped);
    });

    toolbar.appendChild(wrapBtn);
  }
}

function initializeFocusMode(editor) {
  const toolbar = document.querySelector(".toolbar");
  if (toolbar) {
    const focusBtn = document.createElement("button");
    focusBtn.type = "button";
    focusBtn.className = "btn btn-outline-secondary btn-sm";
    focusBtn.innerHTML =
      '<i data-lucide="eye" class="inline-block h-4 w-4"></i> Focus';
    focusBtn.title = "Toggle focus mode (hide preview)";

    focusBtn.addEventListener("click", function () {
      toggleFocusMode();
      focusBtn.classList.toggle("active");
    });

    toolbar.appendChild(focusBtn);
  }
}

// Help dialog functionality
function initializeHelpDialog() {
  const toolbar = document.querySelector(".toolbar");
  if (toolbar) {
    const helpBtn = document.createElement("button");
    helpBtn.type = "button";
    helpBtn.className = "btn btn-outline-info btn-sm";
    helpBtn.innerHTML =
      '<i data-lucide="help-circle" class="inline-block h-4 w-4"></i> Help';
    helpBtn.title = "Show keyboard shortcuts and features";

    helpBtn.addEventListener("click", function () {
      showHelpDialog();
    });

    toolbar.appendChild(helpBtn);
  }

  // Also show help with F1 or Ctrl+?
  document.addEventListener("keydown", function (e) {
    if (e.key === "F1" || ((e.ctrlKey || e.metaKey) && e.key === "?")) {
      e.preventDefault();
      showHelpDialog();
    }
  });
}

function showHelpDialog() {
  // Remove existing dialog if present
  const existingDialog = document.getElementById("help-dialog");
  if (existingDialog) {
    existingDialog.remove();
  }

  // Create help dialog
  const dialog = document.createElement("div");
  dialog.id = "help-dialog";
  dialog.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
    `;

  const content = document.createElement("div");
  content.style.cssText = `
        background: white;
        border-radius: 8px;
        padding: 30px;
        max-width: 800px;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    `;

  content.innerHTML = `
        <div style="display: flex; justify-content: between; align-items: center; margin-bottom: 20px;">
            <h3 style="margin: 0; color: var(--text-primary);">Metaproject Editor Help</h3>
            <button id="close-help" style="background: none; border: none; font-size: 24px; cursor: pointer; color: var(--text-secondary);">&times;</button>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
            <div>
                <h4 style="color: var(--status-info); margin-bottom: 15px;">🤖 AI Assistant</h4>
                <div style="background: var(--bg-surface-1); padding: 15px; border-radius: 6px; margin-bottom: 20px;">
                    <strong>Type:</strong> <code>// [your request]</code> then press <strong>Enter</strong><br>
                    <small style="color: var(--text-secondary);">Example: "// write a summary of this document"</small>
                </div>
                
                <h4 style="color: var(--status-info); margin-bottom: 15px;">⌨️ Keyboard Shortcuts</h4>
                <table style="width: 100%; font-size: 14px;">
                    <tr><td><kbd>Ctrl/Cmd + B</kbd></td><td>Bold text</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + I</kbd></td><td>Italic text</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + K</kbd></td><td>Insert link</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + \`</kbd></td><td>Inline code</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + Shift + C</kbd></td><td>Code block</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + Shift + L</kbd></td><td>Bullet list</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + Shift + O</kbd></td><td>Numbered list</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + D</kbd></td><td>Duplicate line</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + /</kbd></td><td>Toggle comment</td></tr>
                    <tr><td><kbd>Ctrl/Cmd + G</kbd></td><td>Go to line</td></tr>
                    <tr><td><kbd>Alt + ↑/↓</kbd></td><td>Move line up/down</td></tr>
                    <tr><td><kbd>Tab</kbd></td><td>Indent</td></tr>
                    <tr><td><kbd>Shift + Tab</kbd></td><td>Unindent</td></tr>
                </table>
            </div>
            
            <div>
                <h4 style="color: var(--status-info); margin-bottom: 15px;">🚀 Smart Features</h4>
                <ul style="list-style: none; padding: 0;">
                    <li style="margin-bottom: 10px;">✨ <strong>Auto-pairing:</strong> Brackets, quotes automatically close</li>
                    <li style="margin-bottom: 10px;">📝 <strong>Smart lists:</strong> Press Enter to continue lists</li>
                    <li style="margin-bottom: 10px;">🎯 <strong>Auto-complete:</strong> Type <code>/</code> for suggestions</li>
                    <li style="margin-bottom: 10px;">🔄 <strong>Draggable splitter:</strong> Resize editor/preview panes</li>
                    <li style="margin-bottom: 10px;">👁️ <strong>Focus mode:</strong> Hide preview to focus on writing</li>
                    <li style="margin-bottom: 10px;">📏 <strong>Word wrap:</strong> Toggle line wrapping</li>
                </ul>
                
                <h4 style="color: var(--status-info); margin-bottom: 15px;">📊 Mermaid Diagrams</h4>
                <div style="background: var(--bg-surface-1); padding: 15px; border-radius: 6px; margin-bottom: 20px;">
                    <strong>Supported types:</strong><br>
                    • Flowcharts • Sequence diagrams<br>
                    • Pie charts • Gantt charts<br>
                    <small style="color: var(--text-secondary);">Use <code>/</code> autocomplete for quick insertion</small>
                </div>
                
                <h4 style="color: var(--status-info); margin-bottom: 15px;">🎨 Styling</h4>
                <ul style="list-style: none; padding: 0;">
                    <li style="margin-bottom: 8px;">🎭 Theme toggle (dark/light)</li>
                    <li style="margin-bottom: 8px;">📄 Custom stylesheets</li>
                    <li style="margin-bottom: 8px;">🖨️ PDF export with styling</li>
                </ul>
            </div>
        </div>
        
        <div style="text-align: center; margin-top: 25px; padding-top: 20px; border-top: 1px solid var(--border);">
            <p style="color: var(--text-secondary); margin: 0;">Press <kbd>F1</kbd> or <kbd>Ctrl/Cmd + ?</kbd> to show this help anytime</p>
        </div>
    `;

  dialog.appendChild(content);
  document.body.appendChild(dialog);

  // Close dialog handlers — always remove esc listener on dispose to avoid leak.
  function closeOnEscape(e) {
    if (e.key === "Escape") {
      disposeDialog();
    }
  }
  function disposeDialog() {
    dialog.remove();
    document.removeEventListener("keydown", closeOnEscape);
  }

  const closeBtn = document.getElementById("close-help");
  closeBtn.addEventListener("click", disposeDialog);

  dialog.addEventListener("click", function (e) {
    if (e.target === dialog) {
      disposeDialog();
    }
  });

  document.addEventListener("keydown", closeOnEscape);
}

// Helper functions
function wrapSelectedText(editor, prefix, suffix) {
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const selectedText = editor.value.substring(start, end);
  const replacement = prefix + selectedText + suffix;

  insertText(editor, replacement);

  // Select the wrapped content (excluding wrapper)
  if (selectedText) {
    editor.setSelectionRange(start + prefix.length, end + prefix.length);
  } else {
    editor.setSelectionRange(start + prefix.length, start + prefix.length);
  }
}

function insertText(editor, text) {
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const before = editor.value.substring(0, start);
  const after = editor.value.substring(end);

  editor.value = before + text + after;
  editor.setSelectionRange(start + text.length, start + text.length);

  // Trigger input event for auto-save and preview update
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

function insertLink(editor) {
  const selectedText = editor.value.substring(
    editor.selectionStart,
    editor.selectionEnd,
  );
  const linkText = selectedText || "Link text";
  const url = prompt("Enter URL:", "https://");

  if (url) {
    wrapSelectedText(editor, `[${linkText}](`, ")");
    if (!selectedText) {
      // Position cursor to edit link text
      const start = editor.selectionStart - url.length - 3 - linkText.length;
      editor.setSelectionRange(start, start + linkText.length);
    }
  }
}

function insertListItem(editor, marker) {
  const start = editor.selectionStart;
  const lineStart = editor.value.lastIndexOf("\n", start - 1) + 1;
  const beforeLine = editor.value.substring(0, lineStart);
  const afterCursor = editor.value.substring(start);

  editor.value = beforeLine + marker + afterCursor;
  editor.setSelectionRange(
    lineStart + marker.length,
    lineStart + marker.length,
  );
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

function moveLine(editor, direction) {
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const lines = editor.value.split("\n");
  const startLine = editor.value.substring(0, start).split("\n").length - 1;
  const endLine = editor.value.substring(0, end).split("\n").length - 1;

  if (
    (direction === -1 && startLine === 0) ||
    (direction === 1 && endLine === lines.length - 1)
  ) {
    return; // Can't move beyond bounds
  }

  const targetLine = startLine + direction;

  // Swap lines
  if (direction === -1) {
    [lines[startLine - 1], lines[startLine]] = [
      lines[startLine],
      lines[startLine - 1],
    ];
  } else {
    [lines[endLine], lines[endLine + 1]] = [lines[endLine + 1], lines[endLine]];
  }

  editor.value = lines.join("\n");

  // Restore selection
  const newStart =
    lines.slice(0, targetLine).join("\n").length + (targetLine > 0 ? 1 : 0);
  const newEnd = newStart + lines[targetLine].length;
  editor.setSelectionRange(newStart, newEnd);
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

function duplicateLine(editor) {
  const start = editor.selectionStart;
  const lineStart = editor.value.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = editor.value.indexOf("\n", start);
  const line = editor.value.substring(
    lineStart,
    lineEnd === -1 ? editor.value.length : lineEnd,
  );

  const insertPos = lineEnd === -1 ? editor.value.length : lineEnd;
  const before = editor.value.substring(0, insertPos);
  const after = editor.value.substring(insertPos);

  editor.value = before + "\n" + line + after;
  editor.setSelectionRange(insertPos + 1, insertPos + 1 + line.length);
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

function toggleComment(editor) {
  const start = editor.selectionStart;
  const lineStart = editor.value.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = editor.value.indexOf("\n", start);
  const line = editor.value.substring(
    lineStart,
    lineEnd === -1 ? editor.value.length : lineEnd,
  );

  let newLine;
  if (line.startsWith("<!-- ") && line.endsWith(" -->")) {
    // Uncomment
    newLine = line.substring(5, line.length - 4);
  } else {
    // Comment
    newLine = "<!-- " + line + " -->";
  }

  const before = editor.value.substring(0, lineStart);
  const after = editor.value.substring(
    lineEnd === -1 ? editor.value.length : lineEnd,
  );

  editor.value = before + newLine + after;
  editor.setSelectionRange(start, start);
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

function indentSelection(editor, indent) {
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const selectedText = editor.value.substring(start, end);
  const lines = selectedText.split("\n");

  const modifiedLines = lines.map((line) => {
    if (indent) {
      return "  " + line; // Add 2 spaces
    } else {
      return line.startsWith("  ") ? line.substring(2) : line; // Remove 2 spaces
    }
  });

  const newText = modifiedLines.join("\n");
  editor.setSelectionRange(start, end);
  insertText(editor, newText);
}

function toggleLineNumbers(editor) {
  // This would require more complex implementation with CSS and HTML changes
  showNotification("Line numbers feature coming soon!", "info");
}

function showGoToLineDialog(editor) {
  const lineNumber = prompt("Go to line number:");
  if (lineNumber && !isNaN(lineNumber)) {
    const lines = editor.value.split("\n");
    const targetLine = Math.max(
      1,
      Math.min(parseInt(lineNumber), lines.length),
    );
    const position =
      lines.slice(0, targetLine - 1).join("\n").length +
      (targetLine > 1 ? 1 : 0);

    editor.setSelectionRange(position, position);
    editor.focus();
    showNotification(`Jumped to line ${targetLine}`, "success");
  }
}

function toggleFocusMode() {
  const previewPane = document.getElementById("markdown-preview");
  const editorPane = document.getElementById("editor-pane");

  if (!previewPane || !editorPane) {
    return;
  }

  const isHidden = previewPane.style.display === "none";

  if (isHidden) {
    // Show preview
    previewPane.style.display = "";
    editorPane.style.width = "50%";
    previewPane.style.width = "50%";
  } else {
    // Hide preview
    previewPane.style.display = "none";
    editorPane.style.width = "100%";
  }
}

// Helper function to show notifications
function showNotification(message, type = "info") {
  const _t = String(type || "").toLowerCase();
  const variant =
    {
      danger: "error",
      error: "error",
      warning: "warning",
      info: "info",
      success: "success",
    }[_t] || "success";
  if (window.Alpine && window.Alpine.store && window.Alpine.store("ui")) {
    window.Alpine.store("ui").toast({ message, variant });
  } else {
    console.log("[toast:" + variant + "]", message);
  }
}

// Utility Functions
function showToast(message, type = "info") {
  // Routes to the Alpine $store.ui toast queue (rendered by the global
  // toast container in base.html). Replaces the old bootstrap.Toast path.
  const variant = type === "error" ? "error" : type || "info";
  const store =
    window.Alpine && window.Alpine.store && window.Alpine.store("ui");
  if (store) {
    store.toast({ message: message, variant: variant });
  } else {
    // Alpine not ready yet — fail soft rather than throw.
    console.warn("[toast]", variant, message);
  }
}

function showSaveStatus(message, type = "success") {
  let saveStatus = document.getElementById("save-status");

  if (!saveStatus) {
    saveStatus = document.createElement("div");
    saveStatus.id = "save-status";
    saveStatus.className = "save-status";
    document.body.appendChild(saveStatus);
  }

  const iconName = type === "success" ? "check-circle" : "alert-circle";
  const bgClass = type === "success" ? "bg-success" : "bg-warning";

  saveStatus.innerHTML = `
        <div class="alert ${bgClass} text-white alert-dismissible fade show" role="alert">
            <i data-lucide="${iconName}" class="inline-block h-4 w-4" aria-hidden="true"></i> ${message}
        </div>
    `;
  if (window.Lucide) window.Lucide.createIcons({ icons: window.Lucide.icons });

  saveStatus.classList.remove("hidden");

  // Auto-hide after 3 seconds
  setTimeout(() => {
    saveStatus.classList.add("hidden");
  }, 3000);
}

// Form validation helpers
function validateForm(formId) {
  const form = document.getElementById(formId);
  if (!form) return false;

  const inputs = form.querySelectorAll("input[required], textarea[required]");
  let isValid = true;

  inputs.forEach((input) => {
    if (!input.value.trim()) {
      input.classList.add("is-invalid");
      isValid = false;
    } else {
      input.classList.remove("is-invalid");
    }
  });

  return isValid;
}

// AJAX helpers
function makeRequest(url, options = {}) {
  const defaultOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  const config = { ...defaultOptions, ...options };

  return fetch(url, config)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Request failed:", error);
      showToast("Request failed: " + error.message, "danger");
      throw error;
    });
}

// Export functions for use in other scripts
window.MarkdownEditor = {
  setTheme,
  showToast,
  showSaveStatus,
  validateForm,
  makeRequest,
};

// Chat variables - initialize them here only if not already defined
if (typeof conversationHistory === "undefined") {
  window.conversationHistory = [];
}
if (typeof currentViewMode === "undefined") {
  window.currentViewMode = "preview"; // 'preview', 'chat', or 'both'
}

// 1. Force React agent mode and remove toggle
let useReactAgent = true; // Always use React agent

// Remove the toggleReactAgent function and any UI for switching modes
// Remove the toggle button from the DOM if it exists
window.toggleReactAgent = undefined;
const reactAgentToggle = document.getElementById("react-agent-toggle");
if (reactAgentToggle) {
  reactAgentToggle.style.display = "none";
}

// 2. Always use the React agent streaming endpoint
const AI_STREAM_ENDPOINT = "/documents/api/ai-assist-react-stream";

// 3. Update streamAIResponse to use only the React agent endpoint and only update the editor with updated_content
function streamAIResponse(message, documentContent, documentTitle) {
  removeTypingIndicator();
  const streamingMessage = addMessageToChat("assistant", "", true);
  const contentDiv = streamingMessage.querySelector(".message-content");
  let fullResponse = "";
  let finalData = null;

  // Get document ID from the page (try multiple methods)
  let documentId = null;
  const contentEditor = document.getElementById("content");
  if (contentEditor && contentEditor.dataset.documentId) {
    documentId = parseInt(contentEditor.dataset.documentId);
  } else if (window.currentDocumentId) {
    documentId = window.currentDocumentId;
  } else {
    // Try to extract from URL
    const urlParts = window.location.pathname.split("/");
    const editIndex = urlParts.indexOf("edit");
    if (editIndex !== -1 && editIndex + 1 < urlParts.length) {
      documentId = parseInt(urlParts[editIndex + 1]);
    }
  }

  if (!documentId) {
    handleStreamError("Could not determine document ID");
    return;
  }

  // Prepare conversation history
  const messages = [];
  const recentHistory = conversationHistory.slice(-20);
  for (const historyItem of recentHistory) {
    messages.push({
      role: historyItem.role,
      content: historyItem.content,
    });
  }
  messages.push({
    role: "user",
    content: message,
  });

  // Update thinking section with better colors
  updateAIThinking("🔗 Connecting to AI agent...");

  // Store current editor content to preserve it if creating new documents
  const currentEditorContent = contentEditor ? contentEditor.value : "";

  fetch("/documents/api/ai-assist-react-stream", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify({
      document_id: documentId,
      prompt: message,
      conversation_history: messages,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      function readStream() {
        return reader.read().then(({ done, value }) => {
          if (done) {
            // Stream complete: Handle document updates carefully
            // Only update document if it was actually modified (not for READ_ONLY operations)
            if (
              finalData &&
              finalData.updated_content &&
              finalData.document_updated
            ) {
              const editor = document.getElementById("content");
              if (editor) {
                // Check if this was a document creation operation
                const isDocumentCreation =
                  message.toLowerCase().includes("create") &&
                  (message.toLowerCase().includes("document") ||
                    message.toLowerCase().includes("new"));

                // Only update editor if it's NOT a document creation operation
                // For document creation, keep the current document visible
                if (!isDocumentCreation) {
                  editor.value = finalData.updated_content;
                  if (typeof updatePreview === "function") updatePreview();
                  if (typeof updateCounts === "function") updateCounts();
                  if (typeof autoSave === "function") autoSave();
                } else {
                  // For document creation, show a notification but keep current document
                  showNotification(
                    "✅ New document created successfully! The current document remains open.",
                    "success",
                  );
                }
              }
            } else if (
              finalData &&
              finalData.updated_content &&
              !finalData.document_updated
            ) {
              // READ_ONLY operation - document was not modified
            }

            // Show individual tool success indicators only for actual tool operations
            if (
              finalData &&
              finalData.actions_taken &&
              finalData.actions_taken.length > 0
            ) {
              // Filter out READ_ONLY actions that don't actually use tools
              const toolActions = finalData.actions_taken.filter(
                (action) =>
                  !action.includes("Read document content") &&
                  !action.includes("Analyzed document") &&
                  !action.includes("Reviewed document"),
              );

              // Only show tool indicators if there are actual tool operations
              if (toolActions.length > 0) {
                addToolSuccessIndicators(toolActions);
              }
            }

            // Show agent thinking process if available
            if (
              finalData &&
              finalData.intermediate_steps &&
              finalData.intermediate_steps.length > 0
            ) {
              showAgentThinking(finalData.intermediate_steps);
            }

            // Add AI response to conversation history
            if (fullResponse.trim()) {
              conversationHistory.push({
                role: "assistant",
                content: fullResponse.trim(),
              });
            }

            // Re-enable the send button
            const sendBtn = document.getElementById("send-chat-btn");
            if (sendBtn) {
              sendBtn.disabled = false;
              sendBtn.classList.remove("btn-loading");
              sendBtn.innerHTML =
                '<i data-lucide="send" class="inline-block h-4 w-4"></i>';
            }
            return;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === "chunk") {
                  // Hide thinking section on first chunk
                  if (fullResponse === "") {
                    hideAIThinking();
                  }
                  fullResponse += data.content;
                  contentDiv.innerHTML = _sanitizeFormattedHTML(
                    formatMessageContent(fullResponse),
                  );
                } else if (data.type === "complete") {
                  finalData = data;
                  hideAIThinking();
                } else if (data.type === "error") {
                  hideAIThinking();
                  // Check if it's an overload error
                  if (
                    data.message &&
                    (data.message.includes("overloaded") ||
                      data.message.includes("529"))
                  ) {
                    handleStreamError(
                      "🔄 AI service is experiencing high demand. Please try again in a few moments.",
                    );
                  } else {
                    handleStreamError(data.message);
                  }
                  return;
                } else if (data.type === "status") {
                  // Check if it's a retry status with better colors
                  if (data.message && data.message.includes("overloaded")) {
                    updateAIThinking("🔄 AI service busy, retrying...");
                  } else {
                    updateAIThinking(
                      data.message || "🔧 Processing with AI tools...",
                    );
                  }

                  // Add colored status indicator to chat
                  addStatusIndicatorToChat(
                    data.message || "Processing with AI tools...",
                  );
                } else if (data.type === "thinking") {
                  // Handle verbose thinking process messages with enhanced visibility
                  updateAIThinking(data.content || "🧠 AI is processing...");

                  // Add thinking message to chat interface with better styling
                  addThinkingMessageToChat(data.step, data.content);
                }
              } catch (e) {
                console.warn("Failed to parse SSE data:", line, e);
              }
            }
          }
          return readStream();
        });
      }
      return readStream();
    })
    .catch((error) => {
      console.error("Streaming error:", error);
      handleStreamError(error.message);
    });
}

// View toggle functionality for the editor
function toggleView(mode) {
  const container = document.querySelector(".editor-container");
  const previewToggle = document.getElementById("preview-toggle");
  const chatToggle = document.getElementById("chat-toggle");
  const bothToggle = document.getElementById("both-toggle");
  const viewStatus = document.getElementById("view-status");

  // Update view mode
  if (typeof currentViewMode !== "undefined") {
    currentViewMode = mode;
  }

  // Update toggle button states
  if (previewToggle)
    previewToggle.classList.toggle("active", mode === "preview");
  if (chatToggle) chatToggle.classList.toggle("active", mode === "chat");
  if (bothToggle) bothToggle.classList.toggle("active", mode === "both");

  // Remove all mode classes first
  if (container) {
    container.classList.remove("preview-mode", "chat-mode", "both-mode");

    // Add the appropriate mode class
    if (mode === "chat") {
      container.classList.add("chat-mode");
    } else if (mode === "both") {
      container.classList.add("both-mode");
    }
    // Default is preview mode (no class needed due to CSS defaults)
  }

  // Update status bar
  if (viewStatus) {
    const statusText = {
      preview: "Preview",
      chat: "AI Chat",
      both: "Split View",
    };
    const statusIcon = {
      preview: "eye",
      chat: "bot",
      both: "columns-2",
    };
    viewStatus.innerHTML = `
            <i data-lucide="${statusIcon[mode]}" class="inline-block h-3 w-3" aria-hidden="true"></i>
            ${statusText[mode]}
        `;
    if (window.Lucide)
      window.Lucide.createIcons({ icons: window.Lucide.icons });
  }

  // Auto-focus chat input when switching to chat modes and ensure proper scaling
  if (mode === "chat" || mode === "both") {
    setTimeout(() => {
      const chatInput = document.getElementById("ai-chat-input");
      if (chatInput) {
        chatInput.focus();
        // Ensure input scales properly with window size
        chatInput.style.width = "100%";
        chatInput.style.boxSizing = "border-box";
      }
    }, 100);
  }

  // Trigger layout recalculation for responsive behavior
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));

    // Additional check for chat input scaling
    const chatPanel = document.getElementById("ai-chat-panel");
    if (chatPanel && (mode === "chat" || mode === "both")) {
      const chatInput = document.getElementById("ai-chat-input");
      if (chatInput) {
        // Force the input to use full available width
        const inputGroup = chatInput.closest(".input-group");
        if (inputGroup) {
          inputGroup.style.width = "100%";
          inputGroup.style.boxSizing = "border-box";
        }
      }
    }
  }, 150);
}

// Export toggleView function to global scope
window.toggleView = toggleView;

// Chat functionality
function sendQuickMessage(message) {
  const chatInput = document.getElementById("ai-chat-input");
  if (chatInput) {
    chatInput.value = message;
    sendChatMessage();
  }
}

function handleChatKeyPress(event) {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    sendChatMessage();
  }
}

function sendChatMessage() {
  const chatInput = document.getElementById("ai-chat-input");
  const sendBtn = document.getElementById("send-chat-btn");
  const message = chatInput.value.trim();

  if (!message) return;

  // Check for version management commands first
  if (
    typeof handleVersionCommand === "function" &&
    handleVersionCommand(message)
  ) {
    // Command was handled, clear input and return
    chatInput.value = "";
    return;
  }

  // Clear input and show loading animation
  chatInput.value = "";
  if (sendBtn) {
    sendBtn.disabled = true;
    sendBtn.classList.add("btn-loading");
    sendBtn.innerHTML = "";
  }

  // Add user message to chat and conversation history
  addMessageToChat("user", message);
  conversationHistory.push({ role: "user", content: message });

  // Show thinking section
  showAIThinking("Understanding your request...");

  // Show typing indicator
  showTypingIndicator();

  // Get current document content for context
  const documentContent = document.getElementById("content").value;
  const documentTitle = document.getElementById("document-title")
    ? document.getElementById("document-title").textContent
    : "Untitled";

  // Send to AI with streaming using the existing streamAIResponse function
  streamAIResponse(message, documentContent, documentTitle);
}

function addMessageToChat(role, content, isStreaming = false) {
  const messagesContainer = document.getElementById("ai-chat-messages");
  if (!messagesContainer) return null;

  const messageDiv = document.createElement("div");
  messageDiv.className = `ai-message ${role}`;

  if (isStreaming) {
    messageDiv.id = "streaming-message";
  }

  let iconHtml = "";
  if (role === "user") {
    iconHtml = '<i data-lucide="user-circle" class="inline-block h-4 w-4"></i>';
  } else if (role === "assistant") {
    iconHtml = '<i data-lucide="bot" class="inline-block h-4 w-4"></i>';
  } else if (role === "system") {
    iconHtml = '<i data-lucide="info" class="inline-block h-4 w-4"></i>';
  }

  messageDiv.innerHTML = `
        <div class="message-header">
            ${iconHtml}
            <span>${role === "user" ? "You" : role === "assistant" ? "AI Assistant" : "System"}</span>
            <small class="text-muted">${new Date().toLocaleTimeString()}</small>
        </div>
        <div class="message-content" id="message-content-${Date.now()}">
            ${_sanitizeFormattedHTML(formatMessageContent(content))}
        </div>
    `;

  messagesContainer.appendChild(messageDiv);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;

  return messageDiv;
}

// XSS-safe HTML wrapper — every innerHTML site that ingests marked.parse
// output MUST route through this. DOMPurify is loaded by base.html from our
// own origin (vendor-dompurify.js, MPE-557 — it used to come from cdnjs); if
// that script ever fails to load, fall back to textContent so raw HTML from
// streamed LLM responses + user markdown can NEVER reach the DOM
// unsanitized. This fallback is load-bearing, not belt-and-braces.
function _sanitizeFormattedHTML(html) {
  if (
    typeof window !== "undefined" &&
    window.DOMPurify &&
    typeof window.DOMPurify.sanitize === "function"
  ) {
    return window.DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }
  const tmp = document.createElement("div");
  tmp.textContent = html;
  return tmp.innerHTML;
}
window._sanitizeFormattedHTML = _sanitizeFormattedHTML;

// Text-content escape for innerHTML interpolations of trusted-shape but
// untrusted-content strings (LLM intermediate steps, tool names, status
// labels, etc.). Encodes the 5 dangerous chars without HTML-parsing.
function _esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
window._esc = _esc;

function formatMessageContent(content) {
  // Convert markdown to HTML for display
  if (typeof marked !== "undefined") {
    return marked.parse(content);
  }
  // Fallback: basic HTML escaping and line breaks
  return content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

function showTypingIndicator() {
  const typingDiv = document.createElement("div");
  typingDiv.className = "ai-message assistant typing-indicator";
  typingDiv.id = "typing-indicator";

  typingDiv.innerHTML = `
        <div class="message-header">
            <i data-lucide="bot" class="inline-block h-4 w-4"></i>
            <span>AI Assistant</span>
        </div>
        <div class="message-content">
            <div class="typing-indicator">
                <span>AI is thinking</span>
                <div class="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>
    `;

  const messagesContainer = document.getElementById("ai-chat-messages");
  if (messagesContainer) {
    messagesContainer.appendChild(typingDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }
}

function removeTypingIndicator() {
  const indicator = document.getElementById("typing-indicator");
  if (indicator) {
    indicator.remove();
  }
}

function handleStreamError(errorMessage) {
  console.error("Stream error:", errorMessage);
  removeTypingIndicator();
  addMessageToChat("system", `⚠️ Error: ${errorMessage}`);

  // Re-enable the send button
  const sendBtn = document.getElementById("send-chat-btn");
  if (sendBtn) {
    sendBtn.disabled = false;
    sendBtn.classList.remove("btn-loading");
    sendBtn.innerHTML =
      '<i data-lucide="send" class="inline-block h-4 w-4"></i>';
  }
}

// Export chat functions to global scope
window.sendQuickMessage = sendQuickMessage;
window.handleChatKeyPress = handleChatKeyPress;
window.sendChatMessage = sendChatMessage;
window.addMessageToChat = addMessageToChat;
window.formatMessageContent = formatMessageContent;
window.showTypingIndicator = showTypingIndicator;
window.removeTypingIndicator = removeTypingIndicator;
window.handleStreamError = handleStreamError;

// ===============================
// Agent Type and Prompt Template functionality
// ===============================

let currentAgentType = "general"; // Default to general AI
let currentPromptTemplateId = null;
let availablePromptTemplates = [];

// Initialize agents on page load
document.addEventListener("DOMContentLoaded", function () {
  loadAgents();
});

// Load all agents (both general and specialized) into single dropdown
async function loadAgents() {
  // Only run on authenticated pages where the agent dropdown exists
  if (
    !document.getElementById("agent-dropdown-menu") &&
    !document.getElementById("agent-selector")
  )
    return;
  try {
    const response = await fetch("/api/prompt-templates");
    if (!response.ok) {
      console.error("Failed to load agents: HTTP", response.status);
      return;
    }
    const data = await response.json();

    if (data.status === "success") {
      availablePromptTemplates = data.templates;
      populateAgentDropdown();

      // Set default agent (Enhanced General)
      const defaultAgent = availablePromptTemplates.find(
        (t) => t.category === "general" && t.name.includes("Enhanced"),
      );
      if (defaultAgent) {
        currentPromptTemplateId = defaultAgent.id;
        updateAgentSelection(defaultAgent.id);
      }
    }
  } catch (error) {
    console.error("Error loading agents:", error);
    const selector = document.getElementById("agent-selector");
    if (selector) {
      selector.innerHTML = '<option value="">Error loading agents</option>';
    }
  }
}

// Populate single agent dropdown
function populateAgentDropdown() {
  const dropdownMenu = document.getElementById("agent-dropdown-menu");
  const currentAgentName = document.getElementById("current-agent-name");

  if (!dropdownMenu) return;

  if (availablePromptTemplates.length === 0) {
    dropdownMenu.innerHTML =
      '<li><a class="dropdown-item disabled" href="#">No agents available</a></li>';
    return;
  }

  let html = "";

  // General agents
  // Server-controlled today, but admin-editable template names could
  // contain HTML — escape every interpolation into innerHTML.
  const _esc = (s) => {
    const d = document.createElement("div");
    d.textContent = s == null ? "" : String(s);
    return d.innerHTML;
  };

  const generalAgents = availablePromptTemplates.filter(
    (t) => t.category === "general",
  );
  if (generalAgents.length > 0) {
    html += '<li><h6 class="dropdown-header">General Agents</h6></li>';
    generalAgents.forEach((agent) => {
      const active = agent.id === currentPromptTemplateId ? "active" : "";
      const displayName = String(agent.name || "").replace(" (Enhanced)", "");
      html += `<li><a class="dropdown-item ${active}" href="#" onclick="selectAgent(${Number(agent.id)}); return false;">📝 ${_esc(displayName)}</a></li>`;
    });
    html += '<li><hr class="dropdown-divider"></li>';
  }

  // Specialized agents
  const specializedAgents = availablePromptTemplates.filter(
    (t) => t.category === "specialized_agents",
  );
  if (specializedAgents.length > 0) {
    html += '<li><h6 class="dropdown-header">Specialized Agents</h6></li>';
    specializedAgents.forEach((agent) => {
      let icon = "🤖";
      if (agent.name.includes("Architect")) icon = "🏗️";
      else if (agent.name.includes("QA")) icon = "✅";
      else if (agent.name.includes("Project Manager")) icon = "📊";
      else if (agent.name.includes("Developer")) icon = "💻";

      const active = agent.id === currentPromptTemplateId ? "active" : "";
      const displayName = String(agent.name || "")
        .replace(" Agent (Enhanced)", "")
        .replace(" (Enhanced)", "");
      html += `<li><a class="dropdown-item ${active}" href="#" onclick="selectAgent(${Number(agent.id)}); return false;">${icon} ${_esc(displayName)}</a></li>`;
    });
  }

  dropdownMenu.innerHTML = html;

  // Update button text with current agent
  if (currentPromptTemplateId && currentAgentName) {
    const currentAgent = availablePromptTemplates.find(
      (t) => t.id === currentPromptTemplateId,
    );
    if (currentAgent) {
      const displayName = currentAgent.name
        .replace(" (Enhanced)", "")
        .replace(" Agent (Enhanced)", "")
        .replace(" Agent", "");
      currentAgentName.textContent = displayName;
    }
  }
}

// Handle agent selection
async function selectAgent(agentId) {
  if (!agentId) return;

  const agent = availablePromptTemplates.find((t) => t.id == agentId);
  if (!agent) return;

  currentPromptTemplateId = parseInt(agentId);

  // Determine agent type for special handling
  if (agent.category === "specialized_agents") {
    if (agent.name.includes("Architect")) currentAgentType = "architect";
    else if (agent.name.includes("QA")) currentAgentType = "qa";
    else if (agent.name.includes("Project Manager")) currentAgentType = "pm";
    else if (agent.name.includes("Developer")) currentAgentType = "developer";
  } else {
    currentAgentType = "general";
  }

  // Show notification
  const agentName = agent.name.replace(" (Enhanced)", "").replace(" Agent", "");
  showNotification(`🤖 Switched to ${agentName}`, "success");

  // Track usage
  try {
    await fetch(`/api/prompt-templates/${agentId}/use`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    console.error("Error tracking agent usage:", error);
  }
}

// Update agent selection in dropdown
function updateAgentSelection(agentId) {
  const currentAgentName = document.getElementById("current-agent-name");
  if (currentAgentName) {
    const agent = availablePromptTemplates.find((t) => t.id === agentId);
    if (agent) {
      const displayName = agent.name
        .replace(" (Enhanced)", "")
        .replace(" Agent (Enhanced)", "")
        .replace(" Agent", "");
      currentAgentName.textContent = displayName;
    }
  }
  // Re-populate dropdown to update active states
  populateAgentDropdown();
}

// Keep old function for compatibility but redirect to new one
async function loadPromptTemplates() {
  try {
    const response = await fetch("/api/prompt-templates");
    if (!response.ok) {
      console.error("Failed to load prompt templates: HTTP", response.status);
      return;
    }
    const data = await response.json();

    if (data.status === "success") {
      availablePromptTemplates = data.templates;
      populateTemplateDropdown();

      // Set default template if none selected
      if (!currentPromptTemplateId) {
        const defaultTemplate = availablePromptTemplates.find(
          (t) => t.is_default,
        );
        if (defaultTemplate) {
          currentPromptTemplateId = defaultTemplate.id;
          updateDropdownSelection(defaultTemplate.id);
        }
      }
    }
  } catch (error) {
    console.error("Error loading prompt templates:", error);
    const selector = document.getElementById("prompt-template-selector");
    if (selector) {
      selector.innerHTML = '<option value="">Error loading templates</option>';
    }
  }
}

function populateTemplateDropdown() {
  const selector = document.getElementById("prompt-template-selector");
  if (!selector) return;

  if (availablePromptTemplates.length === 0) {
    selector.innerHTML = '<option value="">No templates available</option>';
    return;
  }

  // Group templates by category
  const templatesByCategory = {};
  availablePromptTemplates.forEach((template) => {
    const category = template.category || "general";
    if (!templatesByCategory[category]) {
      templatesByCategory[category] = [];
    }
    templatesByCategory[category].push(template);
  });

  let html = "";

  // Render each category as optgroups.
  // Template names/descriptions/categories are admin-editable strings, so
  // every interpolation into innerHTML must be escaped (mirrors the
  // populateAgentDropdown path). window.escapeHtml is the JS render-boundary
  // helper defined in lucide-init.js.
  const esc = window.escapeHtml || ((s) => (s == null ? "" : String(s)));
  Object.keys(templatesByCategory)
    .sort()
    .forEach((category) => {
      const templates = templatesByCategory[category];
      const categoryTitle =
        category.charAt(0).toUpperCase() + category.slice(1);

      html += `<optgroup label="${esc(categoryTitle)}">`;

      templates.forEach((template) => {
        const description = template.description
          ? ` - ${esc(template.description)}`
          : "";
        const defaultLabel = template.is_default ? " (Default)" : "";
        html += `<option value="${esc(template.id)}">${esc(template.name)}${defaultLabel}${description}</option>`;
      });

      html += `</optgroup>`;
    });

  selector.innerHTML = html;
}

function updateDropdownSelection(templateId) {
  const selector = document.getElementById("prompt-template-selector");
  if (selector) {
    selector.value = templateId;
  }
}

async function selectPromptTemplateFromDropdown(templateId) {
  if (!templateId) return;

  try {
    // Update usage count
    await fetch(`/api/prompt-templates/${templateId}/use`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Update current selection
    currentPromptTemplateId = parseInt(templateId);
    const selectedTemplate = availablePromptTemplates.find(
      (t) => t.id === currentPromptTemplateId,
    );

    if (selectedTemplate) {
      showNotification(
        `✅ Switched to "${selectedTemplate.name}" template`,
        "success",
      );
    }
  } catch (error) {
    console.error("Error selecting prompt template:", error);
    showNotification("❌ Error selecting template", "error");
  }
}

// ===============================
// AI Thinking Section functionality
// ===============================

function showAIThinking(message = "Analyzing your request...") {
  const thinkingSection = document.getElementById("ai-thinking-section");
  const thinkingText = document.getElementById("ai-thinking-text");

  if (thinkingSection && thinkingText) {
    thinkingText.textContent = message;
    thinkingSection.style.display = "block";

    // Scroll to show the thinking section
    thinkingSection.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
}

function updateAIThinking(message) {
  const thinkingText = document.getElementById("ai-thinking-text");
  if (thinkingText) {
    thinkingText.textContent = message;
  }
}

function hideAIThinking() {
  const thinkingSection = document.getElementById("ai-thinking-section");
  if (thinkingSection) {
    // Fade out with animation
    thinkingSection.style.opacity = "0";
    thinkingSection.style.transition = "opacity 0.3s ease-out";

    setTimeout(() => {
      thinkingSection.style.display = "none";
      thinkingSection.style.opacity = "1";
      thinkingSection.style.transition = "";
    }, 300);
  }
}

// Agent type selection function
function selectAgentType(agentType) {
  if (!agentType) return;

  currentAgentType = agentType;

  // Show appropriate message based on agent type
  let agentName = "General AI";
  let agentDescription = "";

  switch (agentType) {
    case "architect":
      agentName = "Software Architect";
      agentDescription =
        "System design, architecture patterns, technology decisions";
      break;
    case "qa":
      agentName = "QA Engineer";
      agentDescription =
        "Test planning, quality assurance, validation strategies";
      break;
    case "pm":
      agentName = "Project Manager";
      agentDescription = "Planning, coordination, progress tracking";
      break;
    case "developer":
      agentName = "Software Developer";
      agentDescription = "Implementation details, code structure, API design";
      break;
    default:
      agentDescription = "General-purpose AI for all document tasks";
  }

  // Update the template selector to show appropriate templates.
  // The selector may be absent on pages that surface the agent picker but
  // not the legacy template <select> — guard every dereference (mirrors the
  // null checks in populateTemplateDropdown / updateDropdownSelection).
  const templateSelector = document.getElementById("prompt-template-selector");
  if (agentType !== "general") {
    // For specialized agents, find and select the corresponding template
    const specializedTemplate = availablePromptTemplates.find(
      (t) =>
        t.category === "specialized_agents" &&
        t.name.toLowerCase().includes(agentType),
    );

    if (specializedTemplate) {
      currentPromptTemplateId = specializedTemplate.id;
      showNotification(
        `🤖 Switched to ${agentName} Agent - ${agentDescription}`,
        "info",
      );
    }
    if (templateSelector) templateSelector.style.display = "none";
  } else {
    // For general agent, show template selector
    if (templateSelector) templateSelector.style.display = "block";
    // Select the enhanced general template if available
    const generalTemplate = availablePromptTemplates.find(
      (t) => t.category === "general" && t.name.includes("Enhanced"),
    );
    if (generalTemplate) {
      currentPromptTemplateId = generalTemplate.id;
      updateDropdownSelection(generalTemplate.id);
    }
    showNotification(`✅ Using General AI Assistant`, "success");
  }
}

// Export functions to global scope
window.loadAgents = loadAgents;
window.selectAgent = selectAgent;
window.selectAgentType = selectAgentType;
window.loadPromptTemplates = loadPromptTemplates;
window.selectPromptTemplateFromDropdown = selectPromptTemplateFromDropdown;
window.showAIThinking = showAIThinking;
window.updateAIThinking = updateAIThinking;
window.hideAIThinking = hideAIThinking;

function addCompactSuccessIndicator() {
  const messagesContainer = document.getElementById("ai-chat-messages");
  if (!messagesContainer) return;

  // Create a more visible success indicator with color
  const successDiv = document.createElement("div");
  successDiv.className = "compact-success-indicator";
  successDiv.innerHTML = `
        <div class="success-tick">
            <i data-lucide="check-circle-2" class="inline-block h-4 w-4" style="color: var(--status-success); font-size: 1rem;"></i>
            <span style="font-size: 0.8rem; color: var(--status-success); margin-left: 6px; font-weight: 500;">Tool executed successfully</span>
        </div>
    `;

  // Add enhanced styling with better visibility
  successDiv.style.cssText = `
        margin: 4px 12px 6px 12px;
        padding: 6px 12px;
        text-align: right;
        opacity: 1;
        font-size: 0.8rem;
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.15), rgba(25, 135, 84, 0.1));
        border-left: 3px solid var(--status-success);
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(40, 167, 69, 0.2);
        animation: successPulse 1s ease-out;
    `;

  messagesContainer.appendChild(successDiv);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;

  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (successDiv.parentNode) {
      successDiv.style.transition =
        "opacity 0.5s ease-out, transform 0.5s ease-out";
      successDiv.style.opacity = "0";
      successDiv.style.transform = "translateX(10px)";
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.remove();
        }
      }, 500);
    }
  }, 5000);
}

// Export the new function
window.addCompactSuccessIndicator = addCompactSuccessIndicator;

function showAgentThinking(intermediateSteps) {
  if (!intermediateSteps || intermediateSteps.length === 0) return;

  const messagesContainer = document.getElementById("ai-chat-messages");
  if (!messagesContainer) return;

  // Create thinking details container
  const thinkingDiv = document.createElement("div");
  thinkingDiv.className = "agent-thinking-details";
  thinkingDiv.innerHTML = `
        <div class="thinking-header">
            <i data-lucide="lightbulb" class="inline-block h-4 w-4" style="color: var(--status-warning);"></i>
            <span style="font-size: 0.8rem; color: var(--text-secondary); margin-left: 4px;">Agent Reasoning</span>
            <button class="btn btn-sm btn-outline-secondary ms-2" onclick="toggleThinkingDetails(this)" style="font-size: 0.7rem; padding: 2px 6px;">
                <i data-lucide="chevron-down" class="inline-block h-4 w-4"></i> Show Details
            </button>
        </div>
        <div class="thinking-content" style="display: none;">
            ${intermediateSteps
              .map(
                (step, index) => `
                <div class="thinking-step">
                    <div class="step-header">
                        <span class="step-number">${index + 1}</span>
                        <span class="step-text">${_esc(typeof step === "string" ? step : JSON.stringify(step))}</span>
                    </div>
                </div>
            `,
              )
              .join("")}
        </div>
    `;

  // Add styling
  thinkingDiv.style.cssText = `
        margin: 4px 12px;
        padding: 6px 10px;
        background: rgba(255, 193, 7, 0.1);
        border: 1px solid rgba(255, 193, 7, 0.3);
        border-radius: 6px;
        font-size: 0.75rem;
        color: var(--text-secondary);
    `;

  messagesContainer.appendChild(thinkingDiv);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

function toggleThinkingDetails(button) {
  const thinkingDiv = button.closest(".agent-thinking-details");
  const content = thinkingDiv.querySelector(".thinking-content");

  if (content.style.display === "none") {
    content.style.display = "block";
    button.innerHTML =
      '<i data-lucide="chevron-up" class="inline-block h-4 w-4"></i> Hide Details';
  } else {
    content.style.display = "none";
    button.innerHTML =
      '<i data-lucide="chevron-down" class="inline-block h-4 w-4"></i> Show Details';
  }
  if (window.Lucide) window.Lucide.createIcons({ icons: window.Lucide.icons });
}

function addThinkingMessageToChat(step, content) {
  const messagesContainer = document.getElementById("ai-chat-messages");
  if (!messagesContainer) {
    console.error("ai-chat-messages container not found");
    return;
  }

  // Create thinking message element
  const thinkingDiv = document.createElement("div");
  thinkingDiv.className = "thinking-message";

  // Define step labels with emojis for better visual feedback
  const stepLabels = {
    initialization: "🤖 Initializing",
    attempt_start: "🔄 Starting Attempt",
    document_analysis: "📄 Analyzing Document",
    prompt_building: "🔨 Building Prompt",
    ai_connection: "📡 Connecting to AI",
    reasoning_start: "🧠 Starting Reasoning",
    decision_making: "🎯 Making Decision",
    response_generation: "💬 Generating Response",
    ai_streaming: "⚡ Streaming Response",
    ai_response_complete: "✅ Response Complete",
    response_parsing: "🔍 Parsing Response",
    action_execution: "⚡ Executing Action",
    verification_start: "🔍 Verifying",
    verification_result: "✅ Verification",
    completion: "🎉 Complete",
    completion_no_update: "✅ Complete",
    overload_error: "⚠️ Service Busy",
    retry_delay: "⏳ Waiting",
    error_handling: "❌ Error Handling",
  };

  const stepLabel = stepLabels[step] || `🧠 ${_esc(String(step))}`;

  thinkingDiv.innerHTML = `
        <div class="thinking-content">
            <div class="thinking-header">
                <i data-lucide="cpu" class="inline-block h-4 w-4" style="color: var(--status-warning); font-size: 0.85rem;"></i>
                <span class="thinking-step" style="color: var(--status-warning); font-weight: 600; font-size: 0.8rem;">${stepLabel}</span>
            </div>
            <div class="thinking-detail" style="color: var(--status-warning); font-size: 0.75rem; margin-top: 2px; margin-left: 20px;">
                ${content ? _sanitizeFormattedHTML(formatMessageContent(content)) : "Processing..."}
            </div>
        </div>
    `;

  // Enhanced styling for better visibility and persistence
  thinkingDiv.style.cssText = `
        margin: 4px 12px 4px 20px;
        padding: 8px 12px;
        background: linear-gradient(135deg, rgba(255, 152, 0, 0.18), rgba(255, 193, 7, 0.12));
        border-left: 4px solid var(--status-warning);
        border-radius: 8px;
        color: var(--status-warning);
        opacity: 1;
        text-indent: 0px;
        box-shadow: 0 2px 4px rgba(255, 152, 0, 0.25);
        animation: thinkingPulse 2s ease-in-out infinite;
        transition: all 0.3s ease;
    `;

  messagesContainer.appendChild(thinkingDiv);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;

  // Increased persistence time to 20 seconds so users can see the reasoning
  setTimeout(() => {
    if (thinkingDiv.parentNode) {
      thinkingDiv.style.transition =
        "opacity 0.8s ease-out, transform 0.8s ease-out";
      thinkingDiv.style.opacity = "0.3";
      thinkingDiv.style.transform = "translateX(-5px)";
      // Don't remove completely, just fade to show the thinking trail
      setTimeout(() => {
        if (thinkingDiv.parentNode) {
          thinkingDiv.style.opacity = "0.15";
          thinkingDiv.style.pointerEvents = "none";
        }
      }, 800);
    }
  }, 20000);
}

function addStatusIndicatorToChat(message) {
  // Create status indicator element as fixed overlay (not in chat container)
  const statusDiv = document.createElement("div");
  statusDiv.className = "status-message tool-status-indicator";
  statusDiv.innerHTML = `
        <div class="status-content">
            <i data-lucide="settings" class="inline-block h-4 w-4"></i>
            <span class="status-text">${_esc(String(message))}</span>
        </div>
    `;

  // Append to body so it appears as overlay
  document.body.appendChild(statusDiv);

  // Auto-remove status messages after 5 seconds
  setTimeout(() => {
    if (statusDiv.parentNode) {
      statusDiv.style.transition =
        "opacity 0.5s ease-out, transform 0.5s ease-out";
      statusDiv.style.opacity = "0";
      statusDiv.style.transform = "translateX(100%)";
      setTimeout(() => {
        if (statusDiv.parentNode) {
          statusDiv.remove();
        }
      }, 500);
    }
  }, 5000);
}

// Export the new functions
window.showAgentThinking = showAgentThinking;
window.toggleThinkingDetails = toggleThinkingDetails;
window.addThinkingMessageToChat = addThinkingMessageToChat;
window.addStatusIndicatorToChat = addStatusIndicatorToChat;

// Test function for status messages
function testStatusMessage() {
  addStatusIndicatorToChat("🧪 Testing status message z-index");
}
window.testStatusMessage = testStatusMessage;

function addToolSuccessIndicators(actionsTaken = []) {
  const messagesContainer = document.getElementById("ai-chat-messages");
  if (!messagesContainer) return;

  // If no actions provided, show a generic success indicator
  if (!actionsTaken || actionsTaken.length === 0) {
    actionsTaken = ["Tool executed successfully"];
  }

  // Create a container for all tool indicators
  const toolsContainer = document.createElement("div");
  toolsContainer.className = "tools-success-container";
  toolsContainer.style.cssText = `
        margin: 4px 0 4px 12px;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
        opacity: 1;
    `;

  // Add individual checkmarks for each tool/action
  actionsTaken.forEach((action, index) => {
    const toolDiv = document.createElement("div");
    toolDiv.className = "tool-success-indicator";

    // Extract tool name from action (e.g., "Updated entire document" -> "Document Update")
    let toolName = action;
    if (action.includes("Updated entire document")) {
      toolName = "Document Update";
    } else if (action.includes("Appended content")) {
      toolName = "Content Append";
    } else if (action.includes("Replaced section")) {
      toolName = "Section Replace";
    } else if (action.includes("Created document")) {
      toolName = "Document Creation";
    } else if (action.includes("Listed documents")) {
      toolName = "Document List";
    } else if (action.includes("Viewed document")) {
      toolName = "Document View";
    } else if (action.includes("Searched for documents")) {
      toolName = "Document Search";
    } else if (action.includes("Got summary")) {
      toolName = "Document Summary";
    } else if (action.includes("Updated description")) {
      toolName = "Description Update";
    } else if (action.includes("Listed all folders")) {
      toolName = "Folder List";
    } else if (action.length > 30) {
      // Truncate long action names
      toolName = action.substring(0, 30) + "...";
    }

    toolDiv.innerHTML = `
            <div class="tool-success-content">
                <i data-lucide="check-circle-2" class="inline-block h-4 w-4" style="color: var(--status-success); font-size: 0.9rem;"></i>
                <span style="font-size: 0.75rem; color: var(--status-success); margin-left: 6px; font-weight: 500;">${_esc(String(toolName))}</span>
            </div>
        `;

    toolDiv.style.cssText = `
            display: flex;
            align-items: center;
            padding: 2px 0;
            opacity: 1;
            animation: toolSuccessSlide 0.3s ease-out ${index * 0.1}s both;
        `;

    toolsContainer.appendChild(toolDiv);
  });

  messagesContainer.appendChild(toolsContainer);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;

  // Auto-remove after 8 seconds
  setTimeout(() => {
    if (toolsContainer.parentNode) {
      toolsContainer.style.transition =
        "opacity 0.5s ease-out, transform 0.5s ease-out";
      toolsContainer.style.opacity = "0";
      toolsContainer.style.transform = "translateX(-10px)";
      setTimeout(() => {
        if (toolsContainer.parentNode) {
          toolsContainer.remove();
        }
      }, 500);
    }
  }, 8000);
}

// Export the new function
window.addToolSuccessIndicators = addToolSuccessIndicators;

// ─── Expose template-facing helpers on window ───────────────────────────
// app.js is bundled as IIFE so top-level `function foo()` declarations stay
// inside the module scope. Template inline scripts (kanban, sprint_kanban,
// admin/holidays, timesheet/*, story_detail …) call these by bare name, so
// each one needs an explicit window binding to survive the bundle wrapper.
window.showCustomConfirm =
  typeof showCustomConfirm !== "undefined"
    ? showCustomConfirm
    : window.showCustomConfirm;
window.confirmAction =
  typeof confirmAction !== "undefined" ? confirmAction : window.confirmAction;
window.confirmDelete =
  typeof confirmDelete !== "undefined" ? confirmDelete : window.confirmDelete;
window.confirmArchive =
  typeof confirmArchive !== "undefined"
    ? confirmArchive
    : window.confirmArchive;
window.confirmThenDispatch =
  typeof confirmThenDispatch !== "undefined"
    ? confirmThenDispatch
    : window.confirmThenDispatch;

// ─── A11y: ARIA state sync + keyboard DnD adapter ───────────────────────
// Wave-5 follow-up to result-harden4-a11y. Inline template scripts toggle
// presentation classes (active, collapsed, bg-surface-1 …) but never
// flipped the matching ARIA state. We observe DOM mutations + handle
// keyboard equivalents here so we don't touch the templates.
(function () {
  "use strict";

  // ── Live region for keyboard DnD announcements ────────────────────
  function ensureKbdDndStatus() {
    let el = document.getElementById("kbd-dnd-status");
    if (el) return el;
    el = document.createElement("div");
    el.id = "kbd-dnd-status";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    // Visually hidden but readable by AT
    el.style.cssText =
      "position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden;";
    document.body.appendChild(el);
    return el;
  }
  function announce(msg) {
    const el = ensureKbdDndStatus();
    // Force re-announce even if text identical
    el.textContent = "";
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.textContent = msg;
  }
  window.__a11yAnnounce = announce;

  // ── aria-pressed sync for view-toggle groups ──────────────────────
  // projects/list.html: `.view-toggle-btn` group toggles `bg-surface-1`
  //   on the active button (see setProjectsView in list.html inline JS).
  // projects/story_detail.html: `.pe-tb-btn[data-viewmode]` uses
  //   `.active` class. Editor toolbar buttons (`.pe-tb-btn[data-cmd]`)
  //   use `.active` too — sync them as well so screen readers know
  //   which formatting marks are toggled on.
  function syncViewToggleGroup(root) {
    const group = root.parentElement;
    if (!group) return;
    const btns = group.querySelectorAll(".view-toggle-btn");
    btns.forEach((btn) => {
      const on =
        btn.classList.contains("bg-surface-1") ||
        btn.classList.contains("text-accent") ||
        btn.classList.contains("active");
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }
  function syncViewmodeGroup(root) {
    const group = root.parentElement;
    if (!group) return;
    group.querySelectorAll("[data-viewmode]").forEach((btn) => {
      btn.setAttribute(
        "aria-pressed",
        btn.classList.contains("active") ? "true" : "false",
      );
    });
  }
  function syncSingleAriaPressed(btn) {
    // For standalone toggle buttons that flip `.active` (editor toolbar
    // format btns). Only touch if button already had aria-pressed set
    // by template — don't poison unrelated buttons.
    if (!btn.hasAttribute("aria-pressed")) return;
    btn.setAttribute(
      "aria-pressed",
      btn.classList.contains("active") ? "true" : "false",
    );
  }

  // ── aria-expanded sync for collapse-toggle (epics swim-lanes) ────
  // projects/epics.html: `.collapse-toggle` gets `.collapsed` class when
  // its section folds (toggleSection in inline script). Initial state is
  // aria-expanded="true"; flip whenever the class changes.
  function syncCollapseToggle(btn) {
    btn.setAttribute(
      "aria-expanded",
      btn.classList.contains("collapsed") ? "false" : "true",
    );
  }

  // ── Single MutationObserver wired for all of the above ───────────
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== "attributes" || m.attributeName !== "class") continue;
      const t = m.target;
      if (!(t instanceof Element)) continue;
      if (t.classList.contains("view-toggle-btn")) {
        syncViewToggleGroup(t);
      } else if (t.matches("[data-viewmode]")) {
        syncViewmodeGroup(t);
      } else if (t.classList.contains("collapse-toggle")) {
        syncCollapseToggle(t);
      } else if (t.matches(".pe-tb-btn[aria-pressed]")) {
        syncSingleAriaPressed(t);
      }
    }
  });

  function attachObserver(scope) {
    observer.observe(scope, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });
  }

  function bootA11ySync() {
    attachObserver(document.body);
    // Initial pass — set aria-* to match current DOM state.
    document
      .querySelectorAll(".view-toggle-btn")
      .forEach((b) => syncViewToggleGroup(b));
    document
      .querySelectorAll("[data-viewmode]")
      .forEach((b) => syncViewmodeGroup(b));
    document
      .querySelectorAll(".collapse-toggle")
      .forEach((b) => syncCollapseToggle(b));
    document
      .querySelectorAll(".pe-tb-btn[aria-pressed]")
      .forEach((b) => syncSingleAriaPressed(b));
  }

  // ── Keyboard DnD adapter ─────────────────────────────────────────
  // Targets:
  //   1. `.story-card[draggable="true"][data-story-id]` (kanban cards)
  //   2. `.column-drag-handle` (kanban column reordering — best-effort)
  // Uses existing `moveStory(id, status)` for cross-column moves and
  // direct DOM reordering for in-column moves.
  let grabbed = null; // currently picked-up Element
  let grabbedKind = null; // 'story' | 'column'

  function clearGrab() {
    if (!grabbed) return;
    grabbed.classList.remove("kbd-grabbing");
    grabbed.removeAttribute("aria-grabbed");
    grabbed = null;
    grabbedKind = null;
  }

  function pickUpStory(card) {
    grabbed = card;
    grabbedKind = "story";
    card.classList.add("kbd-grabbing");
    card.setAttribute("aria-grabbed", "true");
    const title = card.getAttribute("aria-label") || "story";
    const col = card.closest(".kanban-column,[data-status]");
    const colLabel = col
      ? col.getAttribute("aria-label") || col.dataset.status || ""
      : "";
    announce(
      "Picked up " +
        title +
        (colLabel ? " in " + colLabel : "") +
        ". Use arrow keys to move, space to drop, escape to cancel.",
    );
  }

  function pickUpColumn(handle) {
    grabbed = handle;
    grabbedKind = "column";
    handle.classList.add("kbd-grabbing");
    handle.setAttribute("aria-grabbed", "true");
    const label = handle.getAttribute("aria-label") || "column";
    announce(
      "Picked up " +
        label +
        ". Left/right arrows to reorder, space to drop, escape to cancel.",
    );
  }

  function dropAt() {
    if (!grabbed) return;
    const title = grabbed.getAttribute("aria-label") || grabbedKind || "item";
    const col = grabbed.closest
      ? grabbed.closest(".kanban-column,[data-status]")
      : null;
    const colLabel = col
      ? col.getAttribute("aria-label") || col.dataset.status || ""
      : "";
    announce("Dropped " + title + (colLabel ? " in " + colLabel : "") + ".");
    clearGrab();
  }

  function cancelGrab() {
    if (!grabbed) return;
    announce("Cancelled.");
    clearGrab();
  }

  function moveStoryWithinColumn(card, dir) {
    const sibling =
      dir === "up" ? card.previousElementSibling : card.nextElementSibling;
    if (!sibling || !sibling.classList.contains("story-card")) {
      announce(dir === "up" ? "At top of column." : "At bottom of column.");
      return;
    }
    if (dir === "up") {
      sibling.before(card);
    } else {
      sibling.after(card);
    }
    card.focus();
    announce(
      "Moved " + (card.getAttribute("aria-label") || "story") + " " + dir + ".",
    );
  }

  function moveStoryAcrossColumn(card, dir) {
    const currentCol = card.closest(".kanban-column,[data-status]");
    if (!currentCol) return;
    const cols = Array.from(
      document.querySelectorAll(
        ".kanban-column[data-status],[data-status].kanban-column",
      ),
    );
    // Fallback: any element with data-status that holds story cards.
    const pool = cols.length
      ? cols
      : Array.from(document.querySelectorAll("[data-status]"));
    const idx = pool.indexOf(currentCol);
    if (idx === -1) return;
    const targetIdx = dir === "left" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= pool.length) {
      announce(
        dir === "left" ? "No column to the left." : "No column to the right.",
      );
      return;
    }
    const target = pool[targetIdx];
    const newStatus = target.dataset.status;
    const storyId = card.dataset.storyId;
    if (storyId && newStatus && typeof window.moveStory === "function") {
      // Server-side move + DOM relocation handled by moveStory().
      window.moveStory(storyId, newStatus);
      const targetLabel = target.getAttribute("aria-label") || newStatus;
      announce(
        "Moved " +
          (card.getAttribute("aria-label") || "story") +
          " to " +
          targetLabel +
          ".",
      );
      // After moveStory() relocates the card, restore focus + grab state.
      setTimeout(() => {
        const moved = document.querySelector(
          '[data-story-id="' + storyId + '"]',
        );
        if (moved) {
          moved.classList.add("kbd-grabbing");
          moved.setAttribute("aria-grabbed", "true");
          moved.focus();
          grabbed = moved;
        }
      }, 0);
    } else {
      // Fallback: DOM-only move (no API call).
      const container = target.querySelector(".kanban-stories") || target;
      container.appendChild(card);
      card.focus();
      announce(
        "Moved to " +
          (target.getAttribute("aria-label") || newStatus || "next column") +
          ".",
      );
    }
  }

  function moveColumn(handle, dir) {
    const col = handle.closest(".kanban-column,[data-column-id]");
    if (!col || !col.parentElement) return;
    const sib =
      dir === "left" ? col.previousElementSibling : col.nextElementSibling;
    if (!sib) {
      announce(
        dir === "left" ? "No column to the left." : "No column to the right.",
      );
      return;
    }
    if (dir === "left") sib.before(col);
    else sib.after(col);
    handle.focus();
    announce("Moved column " + dir + ".");
  }

  document.addEventListener("keydown", function (e) {
    // Bail for typing contexts.
    const target = e.target;
    if (!(target instanceof Element)) return;
    const tag = target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable)
      return;

    const storyCard =
      target.closest && target.closest(".story-card[data-story-id]");
    const colHandle = target.closest && target.closest(".column-drag-handle");

    // Escape: always cancel any in-progress grab.
    if (e.key === "Escape" && grabbed) {
      e.preventDefault();
      cancelGrab();
      return;
    }

    // Space toggles pickup / drop on focused draggable.
    if (e.key === " " || e.code === "Space") {
      if (grabbed) {
        e.preventDefault();
        dropAt();
        return;
      }
      if (storyCard && document.activeElement === storyCard) {
        e.preventDefault();
        pickUpStory(storyCard);
        return;
      }
      if (colHandle && document.activeElement === colHandle) {
        e.preventDefault();
        pickUpColumn(colHandle);
        return;
      }
    }

    // Arrow keys: only when holding something.
    if (!grabbed) return;
    if (grabbedKind === "story") {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        moveStoryWithinColumn(grabbed, "up");
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        moveStoryWithinColumn(grabbed, "down");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveStoryAcrossColumn(grabbed, "left");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        moveStoryAcrossColumn(grabbed, "right");
      }
    } else if (grabbedKind === "column") {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveColumn(grabbed, "left");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        moveColumn(grabbed, "right");
      }
    }
  });

  // Make story cards keyboard-focusable so the picker can target them.
  function makeStoryCardsFocusable() {
    document.querySelectorAll(".story-card[data-story-id]").forEach((card) => {
      if (!card.hasAttribute("tabindex")) card.setAttribute("tabindex", "0");
    });
  }

  // Re-scan on DOM changes (cards re-rendered after filter / move / load).
  const cardObserver = new MutationObserver(() => makeStoryCardsFocusable());

  function bootKbdDnd() {
    ensureKbdDndStatus();
    makeStoryCardsFocusable();
    cardObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      bootA11ySync();
      bootKbdDnd();
    });
  } else {
    bootA11ySync();
    bootKbdDnd();
  }
})();
